import { RIVER_COURSE_SAMPLES, RIVER_PATH, START_AREA_HALF_SIZE, WORLD_SEED, getBaseTerrainHeightAt as getLegacyBaseTerrainHeightAt, getHeightAt as getLegacyHeightAt, getRiverWaterHeightAt as getLegacyRiverWaterHeightAt, getRoadInfluenceAt as getLegacyRoadInfluenceAt, getRoadProfileAt as getLegacyRoadProfileAt, getTerrainSurfaceWeights as getLegacyTerrainSurfaceWeights, } from "./world.js";
/**
 * Generation Version 2 partitions the world into large macro-biome territories.
 * Stable generated IDs deliberately change with this breaking layout revision.
 */
export const WORLD_GENERATION_VERSION = 2;
export const WORLD_GENERATION_SEED = WORLD_SEED;
/** Canonical Phase 9 world: 8,192 m square, centered on the approved starting scene. */
import { WORLD_SIZE_METERS, WORLD_MIN_COORDINATE, WORLD_MAX_COORDINATE } from "./world-bounds.js";
export { WORLD_SIZE_METERS, WORLD_HALF_EXTENT_METERS, WORLD_MIN_COORDINATE, WORLD_MAX_COORDINATE } from "./world-bounds.js";
/** Canonical CPU/GPU streaming identity. Internal terrain tessellation may be smaller. */
export const STREAM_CHUNK_SIZE = 64;
export const WORLD_CHUNK_SIZE = STREAM_CHUNK_SIZE;
export const WORLD_CHUNKS_PER_AXIS = WORLD_SIZE_METERS / WORLD_CHUNK_SIZE;
export const WORLD_MIN_CHUNK_COORDINATE = WORLD_MIN_COORDINATE / WORLD_CHUNK_SIZE;
export const WORLD_MAX_CHUNK_COORDINATE = WORLD_MAX_COORDINATE / WORLD_CHUNK_SIZE - 1;
/** Float32 retains sub-centimeter steps through this range; the bounded world uses only 4 km. */
export const WORLD_FLOAT32_SAFE_COORDINATE_METERS = 65_536;
export const WORLD_STARTING_AREA_PRESERVE_RADIUS = START_AREA_HALF_SIZE;
export const WORLD_STARTING_AREA_BLEND_END = 256;
export const WORLD_POI_CELL_SIZE = 256;
export const WORLD_CHUNK_SUMMARY_SAMPLE_GRID = 5;
/** Weighted, warped macro cells are intentionally much larger than stream chunks. */
export const WORLD_MACRO_BIOME_CELL_SIZE = 768;
export const WORLD_MACRO_BIOME_WARP_AMPLITUDE = 94;
export const WORLD_MACRO_BIOME_MIN_TRANSITION_WIDTH = 48;
export const WORLD_MACRO_BIOME_MAX_TRANSITION_WIDTH = 112;
export const WORLD_BASE_BIOME_IDS = Object.freeze([
    "ASHLANDS",
    "GROUND_ZERO",
    "FERTILE_PRESERVE",
    "NUCLEAR_FOREST",
    "RUINED_CIVILIZATION",
    "INDUSTRIAL_RUINS",
]);
/** Short alias kept convenient for gameplay systems and external generation tools. */
export const BASE_BIOME_IDS = WORLD_BASE_BIOME_IDS;
export const MACRO_BIOME_IDS = WORLD_BASE_BIOME_IDS;
export const BIOME_TERRAIN_PROFILES = Object.freeze({
    ASHLANDS: Object.freeze({ macroHeightAmplitude: 8.2, roughness: 3.3, erosion: 1.65, localNoiseScale: 142, flattening: 0.08, rockExposure: 0.42, soilProfile: "burned" }),
    GROUND_ZERO: Object.freeze({ macroHeightAmplitude: 5.8, roughness: 2.6, erosion: 2.25, localNoiseScale: 96, flattening: 0.04, rockExposure: 0.94, soilProfile: "fused" }),
    FERTILE_PRESERVE: Object.freeze({ macroHeightAmplitude: 6.2, roughness: 1.35, erosion: 0.38, localNoiseScale: 260, flattening: 0.28, rockExposure: 0.08, soilProfile: "fertile" }),
    NUCLEAR_FOREST: Object.freeze({ macroHeightAmplitude: 10.5, roughness: 2.15, erosion: 0.82, localNoiseScale: 205, flattening: 0.05, rockExposure: 0.28, soilProfile: "forest" }),
    RUINED_CIVILIZATION: Object.freeze({ macroHeightAmplitude: 5.5, roughness: 1.1, erosion: 0.55, localNoiseScale: 230, flattening: 0.58, rockExposure: 0.16, soilProfile: "urban" }),
    INDUSTRIAL_RUINS: Object.freeze({ macroHeightAmplitude: 4.6, roughness: 0.9, erosion: 0.46, localNoiseScale: 280, flattening: 0.72, rockExposure: 0.2, soilProfile: "industrial" }),
});
export const WORLD_REGION_IDS = Object.freeze([
    "ASH_FOREST",
    "RIVER_VALLEY",
    "DEAD_FARMLAND",
    "RUINED_SUBURB",
    "CRATER_FIELD",
    "INDUSTRIAL_WASTELAND",
    "AI_EXCAVATION_ZONE",
    "WETWARE_ZONE",
]);
export const WORLD_ROAD_CLASSES = Object.freeze([
    "HIGHWAY",
    "MAJOR_ROAD",
    "SECONDARY_ROAD",
    "LOCAL_ROAD",
    "SERVICE_ROAD",
]);
export const HUMAN_POI_ARCHETYPES = Object.freeze([
    "abandoned_checkpoint",
    "small_ruin",
    "roadside_site",
    "suburban_ruin",
    "industrial_site",
    "gas_station",
]);
export const AI_POI_ARCHETYPES = Object.freeze([
    "wetware_relay",
    "sensor_site",
    "excavation_site",
    "ai_farm",
    "ecological_monitor",
]);
export const WORLD_OVERLAY_IDS = Object.freeze([
    "RIVER",
    "ROAD",
    "HIGHWAY",
    "RADIATION",
    "AI_PROTECTED_AREA",
    "AI_FARM",
    "AI_EXCAVATION",
    "WETWARE_SITE",
    "HUMAN_POI",
]);
const GENERATION_LAYERS = Object.freeze([
    "region_fields",
    "large_features",
    "pois",
    "terrain_surface",
    "geology",
    "vegetation",
]);
const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));
const clamp01 = (value) => clamp(value, 0, 1);
const mix = (start, end, amount) => start + (end - start) * amount;
function smoothstep(start, end, value) {
    const amount = clamp01((value - start) / (end - start));
    return amount * amount * (3 - amount * 2);
}
function smoother(value) {
    return value * value * value * (value * (value * 6 - 15) + 10);
}
function hashUint32(x, z, salt, seed) {
    let value = Math.imul(x | 0, 374_761_393) ^ Math.imul(z | 0, 668_265_263);
    value ^= Math.imul(salt | 0, 1_274_126_177) ^ Math.imul(seed | 0, 1_443_051);
    value = Math.imul(value ^ (value >>> 15), 2_246_822_519);
    value = Math.imul(value ^ (value >>> 13), 3_266_489_917);
    return (value ^ (value >>> 16)) >>> 0;
}
function randomUnit(x, z, salt, seed) {
    return hashUint32(x, z, salt, seed) / 4_294_967_295;
}
/** Smooth deterministic value noise sampled in world space. */
function coherentNoise(x, z, scale, salt, seed) {
    const scaledX = x / scale;
    const scaledZ = z / scale;
    const cellX = Math.floor(scaledX);
    const cellZ = Math.floor(scaledZ);
    const localX = smoother(scaledX - cellX);
    const localZ = smoother(scaledZ - cellZ);
    const north = mix(randomUnit(cellX, cellZ, salt, seed), randomUnit(cellX + 1, cellZ, salt, seed), localX);
    const south = mix(randomUnit(cellX, cellZ + 1, salt, seed), randomUnit(cellX + 1, cellZ + 1, salt, seed), localX);
    return mix(north, south, localZ) * 2 - 1;
}
function fnv1a(value) {
    let hash = 2_166_136_261;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16_777_619);
    }
    return hash >>> 0;
}
function compactHash(value) {
    return fnv1a(value).toString(36).padStart(7, "0");
}
const CANONICAL_MACRO_BIOME_ANCHORS = Object.freeze([
    Object.freeze({ id: "canonical-ashlands-origin", x: 0, z: 0, biome: "ASHLANDS", weight: 420 }),
    Object.freeze({ id: "canonical-ground-zero", x: 1_780, z: 1_560, biome: "GROUND_ZERO", weight: 340 }),
    Object.freeze({ id: "canonical-fertile-preserve", x: -2_720, z: 2_040, biome: "FERTILE_PRESERVE", weight: 335 }),
    Object.freeze({ id: "canonical-nuclear-forest", x: -1_650, z: -1_520, biome: "NUCLEAR_FOREST", weight: 340 }),
    Object.freeze({ id: "canonical-ruined-civilization", x: -1_820, z: 760, biome: "RUINED_CIVILIZATION", weight: 330 }),
    Object.freeze({ id: "canonical-industrial-ruins", x: 2_180, z: -230, biome: "INDUSTRIAL_RUINS", weight: 330 }),
]);
const macroCellCache = new Map();
const MACRO_CELL_CACHE_LIMIT = 2_048;
const macroNeighborhoodCache = new Map();
const MACRO_NEIGHBORHOOD_CACHE_LIMIT = 512;
function selectMacroBiomeForCell(cellX, cellZ, x, z, seed) {
    const wetness = clamp01(0.5 + coherentNoise(x - 251, z + 173, 1_120, 1_411, seed) * 0.5);
    const destruction = clamp01(0.32 + coherentNoise(x - 883, z + 419, 1_240, 1_421, seed) * 0.36
        + radialInfluence(x, z, 1_780, 1_560, 160, 1_420) * 0.52);
    const formerHuman = clamp01(0.38 + coherentNoise(x + 619, z - 311, 1_360, 1_429, seed) * 0.46);
    const aiEcology = clamp01(0.32 + coherentNoise(x - 1_017, z - 613, 1_510, 1_433, seed) * 0.48);
    const dry = 1 - wetness;
    const scores = [
        ["ASHLANDS", 0.86 + dry * 0.54 + destruction * 0.52],
        ["GROUND_ZERO", 0.055 + Math.pow(destruction, 3) * 0.56],
        ["FERTILE_PRESERVE", 0.13 + wetness * wetness * (0.46 + aiEcology * 0.52) * (1 - destruction * 0.7)],
        ["NUCLEAR_FOREST", 0.34 + wetness * 0.66 + (1 - formerHuman) * 0.32],
        ["RUINED_CIVILIZATION", 0.22 + formerHuman * formerHuman * 0.84],
        ["INDUSTRIAL_RUINS", 0.1 + formerHuman * 0.42 + destruction * 0.18],
    ];
    const total = scores.reduce((sum, entry) => sum + entry[1], 0);
    let selection = randomUnit(cellX, cellZ, 1_439, seed) * total;
    for (const [biome, score] of scores) {
        selection -= score;
        if (selection <= 0)
            return biome;
    }
    return "ASHLANDS";
}
function getProceduralMacroCell(cellX, cellZ, seed) {
    const cacheKey = `${seed}:${cellX}:${cellZ}`;
    const cached = macroCellCache.get(cacheKey);
    if (cached)
        return cached;
    const jitterX = (randomUnit(cellX, cellZ, 1_301, seed) - 0.5) * WORLD_MACRO_BIOME_CELL_SIZE * 0.7;
    const jitterZ = (randomUnit(cellX, cellZ, 1_303, seed) - 0.5) * WORLD_MACRO_BIOME_CELL_SIZE * 0.7;
    const x = (cellX + 0.5) * WORLD_MACRO_BIOME_CELL_SIZE + jitterX;
    const z = (cellZ + 0.5) * WORLD_MACRO_BIOME_CELL_SIZE + jitterZ;
    const biome = selectMacroBiomeForCell(cellX, cellZ, x, z, seed);
    const biomeBias = biome === "ASHLANDS" ? 34 : biome === "NUCLEAR_FOREST" ? 24 : biome === "GROUND_ZERO" ? -34 : 0;
    const cell = Object.freeze({
        id: `macro-v${WORLD_GENERATION_VERSION}-s${seed.toString(36)}-c${cellX}:${cellZ}`,
        x,
        z,
        biome,
        weight: (randomUnit(cellX, cellZ, 1_307, seed) - 0.5) * 150 + biomeBias,
    });
    macroCellCache.set(cacheKey, cell);
    while (macroCellCache.size > MACRO_CELL_CACHE_LIMIT) {
        const oldest = macroCellCache.keys().next().value;
        if (oldest === undefined)
            break;
        macroCellCache.delete(oldest);
    }
    return cell;
}
function getMacroCellNeighborhood(centerCellX, centerCellZ, seed) {
    const cacheKey = `${seed}:${centerCellX}:${centerCellZ}`;
    const cached = macroNeighborhoodCache.get(cacheKey);
    if (cached)
        return cached;
    const cells = [];
    for (let offsetZ = -1; offsetZ <= 1; offsetZ += 1) {
        for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
            cells.push(getProceduralMacroCell(centerCellX + offsetX, centerCellZ + offsetZ, seed));
        }
    }
    const neighborhood = Object.freeze(cells);
    macroNeighborhoodCache.set(cacheKey, neighborhood);
    while (macroNeighborhoodCache.size > MACRO_NEIGHBORHOOD_CACHE_LIMIT) {
        const oldest = macroNeighborhoodCache.keys().next().value;
        if (oldest === undefined)
            break;
        macroNeighborhoodCache.delete(oldest);
    }
    return neighborhood;
}
function biomeIndex(biome) {
    switch (biome) {
        case "ASHLANDS": return 0;
        case "GROUND_ZERO": return 1;
        case "FERTILE_PRESERVE": return 2;
        case "NUCLEAR_FOREST": return 3;
        case "RUINED_CIVILIZATION": return 4;
        case "INDUSTRIAL_RUINS": return 5;
    }
}
function macroBoundaryTransitionWidth(primary, secondary, x, z, seed) {
    const primaryIndex = biomeIndex(primary);
    const secondaryIndex = biomeIndex(secondary);
    const lower = Math.min(primaryIndex, secondaryIndex);
    const upper = Math.max(primaryIndex, secondaryIndex);
    const pairBase = lower === 0 && upper === 1
        ? 106
        : lower === 0 && upper === 2
            ? 86
            : lower === 0 && upper === 3
                ? 72
                : lower === 4 && upper === 5
                    ? 52
                    : 68;
    const pairSalt = 1_459 + lower * 37 + upper * 71;
    return clamp(pairBase + coherentNoise(x + pairSalt * 3, z - pairSalt * 5, 520, pairSalt, seed) * 10, WORLD_MACRO_BIOME_MIN_TRANSITION_WIDTH, WORLD_MACRO_BIOME_MAX_TRANSITION_WIDTH);
}
/** One full 5x5 active terrain set at 1 m sampling, with teleport churn headroom. */
const MACRO_SAMPLE_CACHE_LIMIT = 131_072;
const macroSampleCache = new Map();
function macroSampleCacheKey(x, z, seed) {
    if (seed !== WORLD_GENERATION_SEED || !Number.isInteger(x) || !Number.isInteger(z))
        return undefined;
    if (x < WORLD_MIN_COORDINATE || x > WORLD_MAX_COORDINATE || z < WORLD_MIN_COORDINATE || z > WORLD_MAX_COORDINATE)
        return undefined;
    return (x - WORLD_MIN_COORDINATE) * (WORLD_SIZE_METERS + 1) + (z - WORLD_MIN_COORDINATE);
}
/**
 * Deterministic weighted Voronoi with continuous low-frequency boundary warp.
 * Same-biome neighbor cells merge: only the nearest unlike biome can transition.
 */
export function sampleMacroBiomeAt(x, z, seed = WORLD_GENERATION_SEED) {
    assertFiniteCoordinates(x, z);
    const cacheKey = macroSampleCacheKey(x, z, seed);
    const cached = cacheKey === undefined ? undefined : macroSampleCache.get(cacheKey);
    if (cached)
        return cached;
    const warpedX = x + coherentNoise(x - 173, z + 281, 940, 1_271, seed) * WORLD_MACRO_BIOME_WARP_AMPLITUDE;
    const warpedZ = z + coherentNoise(x + 397, z - 229, 1_090, 1_277, seed) * WORLD_MACRO_BIOME_WARP_AMPLITUDE;
    const centerCellX = Math.floor(warpedX / WORLD_MACRO_BIOME_CELL_SIZE);
    const centerCellZ = Math.floor(warpedZ / WORLD_MACRO_BIOME_CELL_SIZE);
    const bestScores = [
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
    ];
    const bestCells = new Array(6);
    for (const cell of getMacroCellNeighborhood(centerCellX, centerCellZ, seed)) {
        const index = biomeIndex(cell.biome);
        const score = Math.hypot(warpedX - cell.x, warpedZ - cell.z) - cell.weight;
        if (score < bestScores[index]) {
            bestScores[index] = score;
            bestCells[index] = cell;
        }
    }
    if (seed === WORLD_GENERATION_SEED) {
        for (const cell of CANONICAL_MACRO_BIOME_ANCHORS) {
            if (Math.abs(warpedX - cell.x) > 2_100 || Math.abs(warpedZ - cell.z) > 2_100)
                continue;
            const index = biomeIndex(cell.biome);
            const score = Math.hypot(warpedX - cell.x, warpedZ - cell.z) - cell.weight;
            if (score < bestScores[index]) {
                bestScores[index] = score;
                bestCells[index] = cell;
            }
        }
    }
    let winnerIndex = 0;
    for (let index = 1; index < bestScores.length; index += 1) {
        if (bestScores[index] < bestScores[winnerIndex])
            winnerIndex = index;
    }
    let neighborIndex = winnerIndex === 0 ? 1 : 0;
    for (let index = 0; index < bestScores.length; index += 1) {
        if (index !== winnerIndex && bestScores[index] < bestScores[neighborIndex])
            neighborIndex = index;
    }
    const winner = bestCells[winnerIndex];
    let neighbor = bestCells[neighborIndex];
    if (neighbor === undefined) {
        search: for (let radius = 2; radius <= 4; radius += 1) {
            for (let offsetZ = -radius; offsetZ <= radius; offsetZ += 1) {
                for (let offsetX = -radius; offsetX <= radius; offsetX += 1) {
                    if (Math.max(Math.abs(offsetX), Math.abs(offsetZ)) !== radius)
                        continue;
                    const cell = getProceduralMacroCell(centerCellX + offsetX, centerCellZ + offsetZ, seed);
                    if (cell.biome === winner.biome)
                        continue;
                    neighbor = cell;
                    bestScores[neighborIndex] = Math.hypot(warpedX - cell.x, warpedZ - cell.z) - cell.weight;
                    break search;
                }
            }
        }
    }
    neighbor ??= getProceduralMacroCell(centerCellX + 5, centerCellZ + 5, seed);
    const primaryGap = Math.max(0, bestScores[neighborIndex] - bestScores[winnerIndex]);
    let thirdScore = Number.POSITIVE_INFINITY;
    for (let index = 0; index < bestScores.length; index += 1) {
        if (index === winnerIndex || index === neighborIndex)
            continue;
        thirdScore = Math.min(thirdScore, bestScores[index]);
    }
    const competitorGap = Math.max(0, thirdScore - bestScores[neighborIndex]);
    // At a Voronoi triple junction the nearest unlike biome changes identity.
    // Fade its contribution to the primary on that runner-up switch, while a
    // winning-pair exchange still reaches the symmetric 50/50 boundary value.
    const pairIsolation = Number.isFinite(competitorGap)
        ? clamp01(competitorGap / Math.max(1e-9, primaryGap + competitorGap))
        : 1;
    const distanceToBoundary = primaryGap * 0.5;
    const pairTransition = distanceToBoundary >= WORLD_MACRO_BIOME_MAX_TRANSITION_WIDTH * 0.5
        ? 0
        : 1 - smoothstep(0, macroBoundaryTransitionWidth(winner.biome, neighbor.biome, x, z, seed) * 0.5, distanceToBoundary);
    const transition = pairTransition * pairIsolation;
    const secondaryWeight = transition * 0.5;
    const sample = Object.freeze({
        primary: winner.biome,
        secondary: transition <= 1e-6 ? null : neighbor.biome,
        transition,
        distanceToBoundary,
        coreStrength: 1 - transition,
        primaryWeight: 1 - secondaryWeight,
        secondaryWeight,
        territoryId: winner.id,
    });
    if (cacheKey !== undefined) {
        // Generational clear avoids an O(1)-iterator allocation for every sample
        // while teleporting into a fresh 5x5 chunk working set.
        if (macroSampleCache.size >= MACRO_SAMPLE_CACHE_LIMIT)
            macroSampleCache.clear();
        macroSampleCache.set(cacheKey, sample);
    }
    return sample;
}
export const sampleWorldMacroBiomeAt = sampleMacroBiomeAt;
export const getWorldMacroBiomeAt = sampleMacroBiomeAt;
export const getMacroBiomeSampleAt = sampleMacroBiomeAt;
function preservationWeight(x, z) {
    const distance = Math.max(Math.abs(x), Math.abs(z));
    return 1 - smoothstep(WORLD_STARTING_AREA_PRESERVE_RADIUS, WORLD_STARTING_AREA_BLEND_END, distance);
}
function radialInfluence(x, z, centerX, centerZ, inner, outer) {
    return 1 - smoothstep(inner, outer, Math.hypot(x - centerX, z - centerZ));
}
function assertFiniteCoordinates(x, z) {
    if (!Number.isFinite(x) || !Number.isFinite(z))
        throw new RangeError("World coordinates must be finite");
}
function assertValidChunk(chunkX, chunkZ) {
    if (!Number.isInteger(chunkX) || !Number.isInteger(chunkZ) || !isWorldChunkInBounds(chunkX, chunkZ)) {
        throw new RangeError(`World chunk ${chunkX}:${chunkZ} is outside the bounded world`);
    }
}
export function isWorldPositionInBounds(x, z) {
    return (Number.isFinite(x) &&
        Number.isFinite(z) &&
        x >= WORLD_MIN_COORDINATE &&
        x < WORLD_MAX_COORDINATE &&
        z >= WORLD_MIN_COORDINATE &&
        z < WORLD_MAX_COORDINATE);
}
export function clampWorldPosition(x, z) {
    assertFiniteCoordinates(x, z);
    const maximum = WORLD_MAX_COORDINATE - 1e-6;
    return [clamp(x, WORLD_MIN_COORDINATE, maximum), clamp(z, WORLD_MIN_COORDINATE, maximum)];
}
export function isWorldChunkInBounds(chunkX, chunkZ) {
    return (Number.isInteger(chunkX) &&
        Number.isInteger(chunkZ) &&
        chunkX >= WORLD_MIN_CHUNK_COORDINATE &&
        chunkX <= WORLD_MAX_CHUNK_COORDINATE &&
        chunkZ >= WORLD_MIN_CHUNK_COORDINATE &&
        chunkZ <= WORLD_MAX_CHUNK_COORDINATE);
}
export function worldToChunkCoordinate(value) {
    if (!Number.isFinite(value))
        throw new RangeError("World coordinate must be finite");
    return Math.floor(value / WORLD_CHUNK_SIZE);
}
export function getWorldChunkCoordinatesAt(x, z) {
    if (!isWorldPositionInBounds(x, z))
        return undefined;
    return Object.freeze({ x: worldToChunkCoordinate(x), z: worldToChunkCoordinate(z) });
}
export function getWorldChunkBounds(chunkX, chunkZ) {
    assertValidChunk(chunkX, chunkZ);
    return Object.freeze({
        minX: chunkX * WORLD_CHUNK_SIZE,
        minZ: chunkZ * WORLD_CHUNK_SIZE,
        maxX: (chunkX + 1) * WORLD_CHUNK_SIZE,
        maxZ: (chunkZ + 1) * WORLD_CHUNK_SIZE,
    });
}
export function getWorldChunkId(chunkX, chunkZ, seed = WORLD_GENERATION_SEED, version = WORLD_GENERATION_VERSION) {
    assertValidChunk(chunkX, chunkZ);
    return `world-v${version}-s${seed.toString(36)}-c${chunkX}:${chunkZ}`;
}
export function createStableWorldObjectId(identityOrKind, chunkX, chunkZ, localIndex, siteId) {
    const identity = typeof identityOrKind === "string"
        ? {
            kind: identityOrKind,
            chunkX: chunkX ?? Number.NaN,
            chunkZ: chunkZ ?? Number.NaN,
            localIndex: localIndex ?? "missing",
            ...(siteId === undefined ? {} : { siteId }),
        }
        : identityOrKind;
    if (!Number.isInteger(identity.chunkX) || !Number.isInteger(identity.chunkZ)) {
        throw new TypeError("Stable world object IDs require integer chunk coordinates");
    }
    const version = identity.generationVersion ?? WORLD_GENERATION_VERSION;
    const seed = identity.seed ?? WORLD_GENERATION_SEED;
    const kind = identity.kind.toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-|-$/g, "") || "object";
    const source = [version, seed, kind, identity.chunkX, identity.chunkZ, identity.siteId ?? "", identity.localIndex].join("|");
    return `wg${version}:${seed.toString(36)}:${kind}:${identity.chunkX}:${identity.chunkZ}:${String(identity.localIndex)}:${compactHash(source)}`;
}
export const getStableWorldObjectId = createStableWorldObjectId;
export function getWorldGenerationMetadata(seed = WORLD_GENERATION_SEED) {
    return Object.freeze({
        generationVersion: WORLD_GENERATION_VERSION,
        worldSeed: seed,
        coordinateSystem: "right-handed-y-up-xz-meters",
        bounds: Object.freeze({ min: WORLD_MIN_COORDINATE, maxExclusive: WORLD_MAX_COORDINATE, size: WORLD_SIZE_METERS }),
        streamingChunkSize: WORLD_CHUNK_SIZE,
        chunksPerAxis: WORLD_CHUNKS_PER_AXIS,
        safePracticalCoordinateMagnitude: WORLD_FLOAT32_SAFE_COORDINATE_METERS,
        regionIds: WORLD_REGION_IDS,
        baseBiomeIds: WORLD_BASE_BIOME_IDS,
        macroBiomeCellSize: WORLD_MACRO_BIOME_CELL_SIZE,
        macroBiomeTransitionWidth: Object.freeze({
            minimum: WORLD_MACRO_BIOME_MIN_TRANSITION_WIDTH,
            maximum: WORLD_MACRO_BIOME_MAX_TRANSITION_WIDTH,
        }),
    });
}
function mainRoadZAt(x, seed) {
    const phase = randomUnit(0, 0, 301, seed) * Math.PI * 2;
    const seededWarp = (Math.sin(x / 850 + phase) - Math.sin(phase)) * 18;
    return 15 + x * 0.58 + Math.sin(x / 420) * 26 + Math.sin(x / 97) * 12 + seededWarp;
}
function mainRoadDerivativeAt(x, seed) {
    const phase = randomUnit(0, 0, 301, seed) * Math.PI * 2;
    return 0.58 + Math.cos(x / 420) * (26 / 420) + Math.cos(x / 97) * (12 / 97) + Math.cos(x / 850 + phase) * (18 / 850);
}
/** Direct highway reference used by semantic infrastructure placement. */
export function getWorldHighwayProfileAt(x, seed = WORLD_GENERATION_SEED) {
    if (!Number.isFinite(x))
        throw new RangeError("World coordinate must be finite");
    const parameter = clamp(x, WORLD_MIN_COORDINATE, WORLD_MAX_COORDINATE);
    return mainRoadProfile(parameter, mainRoadZAt(parameter, seed), seed);
}
function mainRoadProfile(x, z, seed) {
    let parameter = clamp(x, WORLD_MIN_COORDINATE, WORLD_MAX_COORDINATE);
    for (let iteration = 0; iteration < 4; iteration += 1) {
        const centerZ = mainRoadZAt(parameter, seed);
        const derivative = mainRoadDerivativeAt(parameter, seed);
        parameter = clamp(parameter - ((parameter - x) + (centerZ - z) * derivative) / (1 + derivative * derivative), WORLD_MIN_COORDINATE, WORLD_MAX_COORDINATE);
    }
    const centerZ = mainRoadZAt(parameter, seed);
    const derivative = mainRoadDerivativeAt(parameter, seed);
    const dx = x - parameter;
    const dz = z - centerZ;
    const distance = Math.hypot(dx, dz);
    const signedDistance = (dz - derivative * dx) / Math.hypot(1, derivative);
    const width = 25.5 + coherentNoise(parameter, 0, 190, 307, seed) * 1.5;
    return {
        roadId: "heritage_route",
        roadClass: "HIGHWAY",
        x: parameter,
        z: centerZ,
        width,
        distance,
        signedDistance,
        influence: 1 - smoothstep(width * 0.38, width * 0.68, distance),
        heading: Math.atan2(derivative, 1),
    };
}
function segmentRoadProfile(roadId, roadClass, x, z, startX, startZ, endX, endZ, width) {
    const spanX = endX - startX;
    const spanZ = endZ - startZ;
    const lengthSquared = spanX * spanX + spanZ * spanZ;
    const amount = clamp(((x - startX) * spanX + (z - startZ) * spanZ) / lengthSquared, 0, 1);
    const centerX = mix(startX, endX, amount);
    const centerZ = mix(startZ, endZ, amount);
    const distance = Math.hypot(x - centerX, z - centerZ);
    const signedDistance = ((x - centerX) * -spanZ + (z - centerZ) * spanX) / Math.sqrt(lengthSquared);
    return {
        roadId,
        roadClass,
        x: centerX,
        z: centerZ,
        width,
        distance,
        signedDistance,
        influence: 1 - smoothstep(width * 0.38, width * 0.7, distance),
        heading: Math.atan2(spanZ, spanX),
    };
}
function townRoadProfile(roadId, roadClass, x, z, baseHeading, along, lateral, headingOffset, halfLength, width) {
    const baseCosine = Math.cos(baseHeading);
    const baseSine = Math.sin(baseHeading);
    const centerX = -1_820 + baseCosine * along - baseSine * lateral;
    const centerZ = 760 + baseSine * along + baseCosine * lateral;
    const heading = baseHeading + headingOffset;
    const spanX = Math.cos(heading) * halfLength;
    const spanZ = Math.sin(heading) * halfLength;
    return segmentRoadProfile(roadId, roadClass, x, z, centerX - spanX, centerZ - spanZ, centerX + spanX, centerZ + spanZ, width);
}
function regionalGridRoadProfile(roadId, x, z, heading, spacing, phase, strength, majorEvery, majorClass, localClass, majorWidth, localWidth) {
    const cosine = Math.cos(heading);
    const sine = Math.sin(heading);
    const along = x * cosine + z * sine;
    const across = -x * sine + z * cosine;
    const lineIndex = Math.round((across - phase) / spacing);
    const roadAcross = lineIndex * spacing + phase;
    const centerX = along * cosine - roadAcross * sine;
    const centerZ = along * sine + roadAcross * cosine;
    const distance = Math.abs(across - roadAcross);
    const major = Math.abs(lineIndex % majorEvery) === 0;
    const width = major ? majorWidth : localWidth;
    return {
        roadId,
        roadClass: major ? majorClass : localClass,
        x: centerX,
        z: centerZ,
        width,
        distance,
        signedDistance: across - roadAcross,
        influence: (1 - smoothstep(width * 0.38, width * 0.7, distance)) * strength,
        heading,
    };
}
function regionalGridPoiAnchorAt(archetype, x, z, seed) {
    const heading = archetype === "suburban_ruin"
        ? (randomUnit(0, 0, 1_907, seed) - 0.5) * 0.34
        : (randomUnit(0, 0, 1_919, seed) - 0.5) * 0.22 - 0.08;
    const cosine = Math.cos(heading);
    const sine = Math.sin(heading);
    const along = x * cosine + z * sine;
    const across = -x * sine + z * cosine;
    const longitudinalSpacing = archetype === "suburban_ruin" ? 112 : 156;
    const longitudinalPhase = archetype === "suburban_ruin" ? 31 : 47;
    const crossSpacing = archetype === "suburban_ruin" ? 144 : 188;
    const crossPhase = archetype === "suburban_ruin" ? -23 : -39;
    const snappedAcross = Math.round((across - longitudinalPhase) / longitudinalSpacing) * longitudinalSpacing + longitudinalPhase;
    const crossCoordinate = -along;
    const crossIndex = archetype === "suburban_ruin"
        ? Math.round((crossCoordinate - crossPhase) / crossSpacing)
        : Math.round((crossCoordinate - crossPhase) / crossSpacing - 0.5) + 0.5;
    const snappedAlong = -(crossIndex * crossSpacing + crossPhase);
    return Object.freeze({
        x: snappedAlong * cosine - snappedAcross * sine,
        z: snappedAlong * sine + snappedAcross * cosine,
        heading,
    });
}
function regionalGridRoadProfilesAt(x, z, seed) {
    const macro = sampleMacroBiomeAt(x, z, seed);
    const showcaseMasksEnabled = seed === WORLD_GENERATION_SEED;
    const settlementShowcaseMask = showcaseMasksEnabled
        ? Math.min(smoothstep(420, 540, Math.hypot(x + 1_820, z - 760)), smoothstep(105, 165, Math.hypot(x - 1_080, z - (mainRoadZAt(1_080, seed) + 30))))
        : 1;
    const industrialShowcaseMask = showcaseMasksEnabled
        ? smoothstep(105, 180, Math.hypot(x - 2_180, z + 150))
        : 1;
    const settlementStrength = smoothstep(0.42, 0.72, getMacroBiomeWeight(macro, "RUINED_CIVILIZATION")) * settlementShowcaseMask;
    const industrialStrength = smoothstep(0.42, 0.72, getMacroBiomeWeight(macro, "INDUSTRIAL_RUINS")) * industrialShowcaseMask;
    const profiles = [];
    if (settlementStrength > 0) {
        const heading = (randomUnit(0, 0, 1_907, seed) - 0.5) * 0.34;
        profiles.push(regionalGridRoadProfile("settlement_grid_longitudinal", x, z, heading, 112, 31, settlementStrength, 4, "MAJOR_ROAD", "LOCAL_ROAD", 11.5, 6.8), regionalGridRoadProfile("settlement_grid_cross", x, z, heading + Math.PI * 0.5, 144, -23, settlementStrength, 3, "SECONDARY_ROAD", "LOCAL_ROAD", 9.2, 6.4));
    }
    if (industrialStrength > 0) {
        const heading = (randomUnit(0, 0, 1_919, seed) - 0.5) * 0.22 - 0.08;
        profiles.push(regionalGridRoadProfile("industrial_grid_longitudinal", x, z, heading, 156, 47, industrialStrength, 3, "SERVICE_ROAD", "SERVICE_ROAD", 10, 7.8), regionalGridRoadProfile("industrial_grid_cross", x, z, heading + Math.PI * 0.5, 188, -39, industrialStrength, 3, "SERVICE_ROAD", "SERVICE_ROAD", 9.4, 7.4));
    }
    return profiles;
}
function getProceduralRoadProfilesAt(x, z, seed) {
    const main = mainRoadProfile(x, z, seed);
    const industrial = segmentRoadProfile("industrial_spur", "SERVICE_ROAD", x, z, 620, mainRoadZAt(620, seed), 2_560, -260, 8.5);
    const suburbEndZ = mainRoadZAt(-520, seed);
    const townLineAmount = (-1_820 - -2_350) / (-520 - -2_350);
    const suburbStartZ = (760 - townLineAmount * suburbEndZ) / (1 - townLineAmount);
    const suburb = segmentRoadProfile("suburban_connector", "MAJOR_ROAD", x, z, -2_350, suburbStartZ, -520, suburbEndZ, 12);
    const suburbHeading = Math.atan2(suburbEndZ - suburbStartZ, -520 - -2_350);
    const townRoads = [
        townRoadProfile("town_cross", "SECONDARY_ROAD", x, z, suburbHeading, 0, 0, Math.PI * 0.5, 360, 10.5),
        townRoadProfile("town_parallel_north", "LOCAL_ROAD", x, z, suburbHeading, 0, 112, 0, 360, 7.2),
        townRoadProfile("town_parallel_south", "LOCAL_ROAD", x, z, suburbHeading, 0, -112, 0, 360, 7.2),
        townRoadProfile("town_cross_west", "LOCAL_ROAD", x, z, suburbHeading, -185, 0, Math.PI * 0.5, 235, 7),
        townRoadProfile("town_cross_east", "LOCAL_ROAD", x, z, suburbHeading, 185, 0, Math.PI * 0.5, 235, 7),
    ];
    return [main, industrial, suburb, ...townRoads, ...regionalGridRoadProfilesAt(x, z, seed)];
}
function getProceduralRoadProfileAt(x, z, seed) {
    const profiles = getProceduralRoadProfilesAt(x, z, seed);
    let closest = profiles[0];
    for (const profile of profiles.slice(1)) {
        if (profile.distance - profile.width * 0.5 < closest.distance - closest.width * 0.5)
            closest = profile;
    }
    return closest;
}
export function getWorldRoadProfileAt(x, z, seed = WORLD_GENERATION_SEED) {
    assertFiniteCoordinates(x, z);
    const generated = getProceduralRoadProfileAt(x, z, seed);
    if (seed !== WORLD_GENERATION_SEED)
        return generated;
    const retained = preservationWeight(x, z);
    if (retained <= 0)
        return generated;
    const legacy = getLegacyRoadProfileAt(x, z);
    const legacyInfluence = getLegacyRoadInfluenceAt(x, z);
    const signedDistance = Math.sign(x - legacy.x || z - legacy.z || 1) * legacy.distance;
    return Object.freeze({
        roadId: "heritage_route",
        roadClass: "HIGHWAY",
        x: mix(generated.x, legacy.x, retained),
        z: mix(generated.z, legacy.z, retained),
        width: mix(generated.width, legacy.width, retained),
        distance: mix(generated.distance, legacy.distance, retained),
        signedDistance: mix(generated.signedDistance, signedDistance, retained),
        influence: mix(generated.influence, legacyInfluence, retained),
        heading: generated.heading,
    });
}
export function getWorldRoadInfluenceAt(x, z, seed = WORLD_GENERATION_SEED) {
    assertFiniteCoordinates(x, z);
    const generated = Math.max(...getProceduralRoadProfilesAt(x, z, seed).map((profile) => profile.influence));
    if (seed !== WORLD_GENERATION_SEED)
        return generated;
    const retained = preservationWeight(x, z);
    if (retained <= 0)
        return generated;
    return mix(generated, getLegacyRoadInfluenceAt(x, z), retained);
}
export const sampleWorldRoadAt = getWorldRoadProfileAt;
function getLegacyRiverCourseGeometryAt(z) {
    let closest = RIVER_COURSE_SAMPLES[0];
    let bestDistance = Math.abs(z - closest.z);
    for (let index = 0; index < RIVER_COURSE_SAMPLES.length - 1; index += 1) {
        const start = RIVER_COURSE_SAMPLES[index];
        const end = RIVER_COURSE_SAMPLES[index + 1];
        const minimumZ = Math.min(start.z, end.z);
        const maximumZ = Math.max(start.z, end.z);
        if (z >= minimumZ && z <= maximumZ && start.z !== end.z) {
            const amount = (z - start.z) / (end.z - start.z);
            return Object.freeze({ x: mix(start.x, end.x, amount), z, width: mix(start.width, end.width, amount), depth: mix(start.depth, end.depth, amount) });
        }
        const distance = Math.abs(z - end.z);
        if (distance < bestDistance) {
            closest = end;
            bestDistance = distance;
        }
    }
    return Object.freeze({ x: closest.x, z: closest.z, width: closest.width, depth: closest.depth });
}
function getProceduralRiverCourseGeometryAt(z, seed) {
    const originWarp = coherentNoise(0, 0, 860, 401, seed);
    const warp = (coherentNoise(0, z, 860, 401, seed) - originWarp) * 48;
    const x = 8 + z * 0.025 + Math.sin(z / 330) * 31 + Math.sin(z / 121) * 11 + warp;
    const width = 10.5 + coherentNoise(0, z, 260, 409, seed) * 2.25 + Math.sin(z / 510) * 1.15;
    const depth = 2.8 + coherentNoise(0, z, 330, 419, seed) * 0.55;
    return { x, z, width: Math.max(7.2, width), depth: Math.max(2.05, depth) };
}
function getWorldRiverCourseGeometryAt(z, seed) {
    const generated = getProceduralRiverCourseGeometryAt(z, seed);
    if (seed !== WORLD_GENERATION_SEED)
        return generated;
    const legacy = getLegacyRiverCourseGeometryAt(z);
    const retained = preservationWeight(legacy.x, z);
    if (retained <= 0)
        return generated;
    return {
        x: mix(generated.x, legacy.x, retained),
        z,
        width: mix(generated.width, legacy.width, retained),
        depth: mix(generated.depth, legacy.depth, retained),
    };
}
export function getWorldRiverProfileAt(x, z, seed = WORLD_GENERATION_SEED) {
    assertFiniteCoordinates(x, z);
    const course = getWorldRiverCourseGeometryAt(z, seed);
    const signedDistance = x - course.x;
    const distance = Math.abs(signedDistance);
    const excavation = 1 - smoothstep(course.width * 0.41, course.width * 0.96, distance);
    return {
        ...course,
        distance,
        signedDistance,
        excavation,
        influence: 1 - smoothstep(course.width * 0.45, course.width * 1.45, distance),
    };
}
export function getWorldRiverCenterAt(z, seed = WORLD_GENERATION_SEED) {
    if (!Number.isFinite(z))
        throw new RangeError("World coordinate must be finite");
    return getWorldRiverCourseGeometryAt(z, seed).x;
}
export function getWorldRiverWidthAt(z, seed = WORLD_GENERATION_SEED) {
    if (!Number.isFinite(z))
        throw new RangeError("World coordinate must be finite");
    return getWorldRiverCourseGeometryAt(z, seed).width;
}
export const sampleWorldRiverAt = getWorldRiverProfileAt;
function getProceduralBaseTerrainHeightAt(x, z, seed) {
    const warpX = coherentNoise(x, z, 980, 101, seed) * 120;
    const warpZ = coherentNoise(x + 317, z - 181, 1_080, 103, seed) * 120;
    const broad = coherentNoise(x + warpX, z + warpZ, 1_360, 107, seed) * 11;
    const rollingField = coherentNoise(x + warpX, z + warpZ, 410, 109, seed);
    const rolling = rollingField * 4.2;
    const broken = coherentNoise(x - 71, z + 53, 118, 113, seed) * 1.15;
    const detail = broken * 0.18;
    const erodedRidges = Math.pow(Math.max(0, 1 - Math.abs(rollingField)), 3) * 2.2 - 0.4;
    return broad + rolling + broken + detail + erodedRidges;
}
export function getWorldBaseTerrainHeightAt(x, z, seed = WORLD_GENERATION_SEED) {
    assertFiniteCoordinates(x, z);
    if (seed !== WORLD_GENERATION_SEED)
        return getProceduralBaseTerrainHeightAt(x, z, seed);
    const retained = preservationWeight(x, z);
    if (retained >= 1)
        return getLegacyBaseTerrainHeightAt(x, z);
    const generated = getProceduralBaseTerrainHeightAt(x, z, seed);
    if (retained <= 0)
        return generated;
    return mix(generated, getLegacyBaseTerrainHeightAt(x, z), retained);
}
function ellipticalInfluence(x, z, centerX, centerZ, radiusX, radiusZ, rotation, inner = 0) {
    const cosine = Math.cos(rotation);
    const sine = Math.sin(rotation);
    const offsetX = x - centerX;
    const offsetZ = z - centerZ;
    const localX = offsetX * cosine + offsetZ * sine;
    const localZ = -offsetX * sine + offsetZ * cosine;
    return 1 - smoothstep(inner, 1, Math.hypot(localX / radiusX, localZ / radiusZ));
}
function sampleDirectImpactRadiation(x, z, seed) {
    if (Math.abs(x - 1_780) > 760 || Math.abs(z - 1_560) > 760)
        return 0;
    const irregularity = 1 + coherentNoise(x + 83, z - 47, 175, 1_601, seed) * 0.14;
    return clamp01(radialInfluence(x, z, 1_780, 1_560, 72, 660 * irregularity)
        * (0.82 + coherentNoise(x - 31, z + 59, 96, 1_607, seed) * 0.12));
}
/** Spatial hazard overlay: impact core, irregular perimeter, downwind fallout and water transport. */
export function sampleWorldRadiationAt(x, z, seed = WORLD_GENERATION_SEED) {
    assertFiniteCoordinates(x, z);
    const directImpact = sampleDirectImpactRadiation(x, z, seed);
    const fallout = clamp01(ellipticalInfluence(x, z, 840, 1_030, 1_620, 440, -0.32, 0.08)
        * (0.1 + directImpact * 0.7)
        * (0.83 + coherentNoise(x - 337, z + 211, 310, 1_613, seed) * 0.17));
    const river = getWorldRiverProfileAt(x, z, seed);
    const contaminatedWater = clamp01((1 - smoothstep(river.width * 0.65, river.width * 2.8, river.distance))
        * radialInfluence(x, z, 120, 1_050, 0, 1_950)
        * 0.32);
    const intensity = clamp01(Math.max(directImpact, fallout * 0.72, contaminatedWater));
    const level = intensity < 0.025
        ? "none"
        : intensity < 0.2
            ? "low"
            : intensity < 0.46
                ? "medium"
                : intensity < 0.76
                    ? "high"
                    : "extreme";
    return Object.freeze({ intensity, level, directImpact, fallout, contaminatedWater });
}
export function getRadiationIntensity(x, z, seed = WORLD_GENERATION_SEED) {
    return sampleWorldRadiationAt(x, z, seed).intensity;
}
export const getWorldRadiationIntensityAt = getRadiationIntensity;
export function getMacroBiomeWeight(sample, biome) {
    if (sample.primary === biome)
        return sample.primaryWeight;
    if (sample.secondary === biome)
        return sample.secondaryWeight;
    return 0;
}
const ECOLOGICAL_PATROL_POINTS = Object.freeze([
    Object.freeze({ x: -2_860, z: 1_930, radius: 360 }),
    Object.freeze({ x: -2_590, z: 2_010, radius: 390 }),
    Object.freeze({ x: -2_720, z: 2_270, radius: 340 }),
]);
export function sampleWorldEcologyAt(x, z, seed = WORLD_GENERATION_SEED) {
    assertFiniteCoordinates(x, z);
    const macro = sampleMacroBiomeAt(x, z, seed);
    const fertile = getMacroBiomeWeight(macro, "FERTILE_PRESERVE");
    const forest = getMacroBiomeWeight(macro, "NUCLEAR_FOREST");
    const farmProtection = radialInfluence(x, z, -2_720, 2_040, 100, 660);
    const protectionLevel = clamp01(fertile * (0.68 + coherentNoise(x, z, 540, 1_631, seed) * 0.08)
        + forest * 0.18
        + farmProtection * 0.24);
    let monitoringCoverage = 0;
    for (const point of ECOLOGICAL_PATROL_POINTS) {
        monitoringCoverage = Math.max(monitoringCoverage, radialInfluence(x, z, point.x, point.z, point.radius * 0.16, point.radius));
    }
    monitoringCoverage = clamp01(monitoringCoverage * (0.42 + protectionLevel * 0.58));
    return Object.freeze({
        protectionLevel,
        monitoringCoverage,
        protectedArea: protectionLevel >= 0.5,
    });
}
export function ecologicalProtectionLevel(x, z, seed = WORLD_GENERATION_SEED) {
    return sampleWorldEcologyAt(x, z, seed).protectionLevel;
}
export const getEcologicalProtectionLevelAt = ecologicalProtectionLevel;
export function getMonitoringCoverageAt(x, z, seed = WORLD_GENERATION_SEED) {
    return sampleWorldEcologyAt(x, z, seed).monitoringCoverage;
}
const BIOME_FAUNA_PROFILES = Object.freeze({
    ASHLANDS: Object.freeze({ insects: 0.04, birds: 0.06, smallMammals: 0.025, herbivores: 0.01, predators: 0.015 }),
    GROUND_ZERO: Object.freeze({ insects: 0.005, birds: 0.008, smallMammals: 0.003, herbivores: 0, predators: 0.002 }),
    FERTILE_PRESERVE: Object.freeze({ insects: 0.94, birds: 0.88, smallMammals: 0.7, herbivores: 0.54, predators: 0.2 }),
    NUCLEAR_FOREST: Object.freeze({ insects: 0.72, birds: 0.64, smallMammals: 0.66, herbivores: 0.4, predators: 0.48 }),
    RUINED_CIVILIZATION: Object.freeze({ insects: 0.22, birds: 0.34, smallMammals: 0.27, herbivores: 0.05, predators: 0.12 }),
    INDUSTRIAL_RUINS: Object.freeze({ insects: 0.08, birds: 0.13, smallMammals: 0.1, herbivores: 0.015, predators: 0.05 }),
});
export function sampleFaunaProfileAt(x, z, seed = WORLD_GENERATION_SEED) {
    const macro = sampleMacroBiomeAt(x, z, seed);
    const primary = BIOME_FAUNA_PROFILES[macro.primary];
    const secondary = macro.secondary === null ? primary : BIOME_FAUNA_PROFILES[macro.secondary];
    const radiationSuppression = 1 - getRadiationIntensity(x, z, seed) * 0.82;
    return Object.freeze({
        insects: mix(primary.insects, secondary.insects, macro.secondaryWeight) * radiationSuppression,
        birds: mix(primary.birds, secondary.birds, macro.secondaryWeight) * radiationSuppression,
        smallMammals: mix(primary.smallMammals, secondary.smallMammals, macro.secondaryWeight) * radiationSuppression,
        herbivores: mix(primary.herbivores, secondary.herbivores, macro.secondaryWeight) * radiationSuppression,
        predators: mix(primary.predators, secondary.predators, macro.secondaryWeight) * radiationSuppression,
    });
}
export const getWorldFaunaProfileAt = sampleFaunaProfileAt;
/** Spatial overlays remain independent from base-biome ownership. */
export function sampleWorldOverlaysAt(x, z, seed = WORLD_GENERATION_SEED) {
    assertFiniteCoordinates(x, z);
    const overlays = [];
    const river = getWorldRiverProfileAt(x, z, seed);
    const road = getWorldRoadProfileAt(x, z, seed);
    const highway = mainRoadProfile(x, z, seed);
    if (river.distance < river.width * 1.8)
        overlays.push("RIVER");
    if (highway.distance < highway.width * 1.8)
        overlays.push("HIGHWAY");
    else if (road.distance < road.width * 1.8)
        overlays.push("ROAD");
    if (getRadiationIntensity(x, z, seed) >= 0.08)
        overlays.push("RADIATION");
    if (sampleWorldEcologyAt(x, z, seed).protectedArea)
        overlays.push("AI_PROTECTED_AREA");
    if (radialInfluence(x, z, -2_720, 2_040, 0, 110) > 0)
        overlays.push("AI_FARM");
    if (getAiExcavationInfluenceAt(x, z) >= 0.08)
        overlays.push("AI_EXCAVATION");
    if (radialInfluence(x, z, 864, -2_560, 0, 95) > 0
        || radialInfluence(x, z, 28, -48, 0, 55) > 0)
        overlays.push("WETWARE_SITE");
    const nearbyHumanSite = generateWorldPoisInBounds({
        minX: x - 96,
        minZ: z - 96,
        maxX: x + 96,
        maxZ: z + 96,
    }, seed).some((poi) => poi.origin === "human" && Math.hypot(x - poi.x, z - poi.z) <= poi.footprintRadius);
    if (nearbyHumanSite)
        overlays.push("HUMAN_POI");
    return Object.freeze(overlays);
}
export const getWorldOverlaysAt = sampleWorldOverlaysAt;
export function sampleWorldEnvironmentalFieldsAt(x, z, seed = WORLD_GENERATION_SEED) {
    assertFiniteCoordinates(x, z);
    return sampleWorldEnvironmentalFields(x, z, seed, getWorldBaseTerrainHeightAt(x, z, seed), getWorldRiverProfileAt(x, z, seed), getWorldRoadProfileAt(x, z, seed));
}
function sampleWorldEnvironmentalFields(x, z, seed, baseHeight, river, road) {
    const wetField = coherentNoise(x - 251, z + 173, 760, 503, seed);
    const humanField = coherentNoise(x + 619, z - 311, 1_180, 509, seed);
    const destructionField = coherentNoise(x - 883, z + 419, 1_030, 521, seed);
    const aiField = coherentNoise(x - 1_017, z - 613, 1_360, 541, seed);
    const elevation = clamp01(0.5 + baseHeight / 30);
    const wetness = clamp01(0.43 + wetField * 0.34 + river.influence * 0.52 - elevation * 0.14);
    const formerHumanLandUse = clamp01(0.35 + humanField * 0.38 + smoothstep(220, 0, road.distance) * 0.32);
    const destruction = clamp01(0.38 + destructionField * 0.4 + radialInfluence(x, z, 1_700, 1_450, 150, 1_480) * 0.5);
    const radiation = getRadiationIntensity(x, z, seed);
    const aiActivity = clamp01(0.16 + aiField * 0.28 + radialInfluence(x, z, 1_750, -1_850, 120, 1_500) * 0.62);
    const settlementProximity = clamp01(0.2 + humanField * 0.2 + formerHumanLandUse * 0.52 + smoothstep(320, 0, road.distance) * 0.12);
    const flatness = clamp01(0.78 - Math.abs(humanField - destructionField) * 0.28);
    return {
        elevation,
        wetness,
        formerHumanLandUse,
        destruction,
        radiation,
        aiActivity,
        settlementProximity,
        flatness,
        roadProximity: 1 - smoothstep(35, 260, road.distance),
        riverProximity: river.influence,
    };
}
const LEGACY_REGION_BY_BIOME = Object.freeze({
    ASHLANDS: "ASH_FOREST",
    GROUND_ZERO: "CRATER_FIELD",
    FERTILE_PRESERVE: "DEAD_FARMLAND",
    NUCLEAR_FOREST: "RIVER_VALLEY",
    RUINED_CIVILIZATION: "RUINED_SUBURB",
    INDUSTRIAL_RUINS: "INDUSTRIAL_WASTELAND",
});
function projectMacroBiomeToLegacyRegions(sample) {
    const values = {
        ASH_FOREST: 0,
        RIVER_VALLEY: 0,
        DEAD_FARMLAND: 0,
        RUINED_SUBURB: 0,
        CRATER_FIELD: 0,
        INDUSTRIAL_WASTELAND: 0,
        AI_EXCAVATION_ZONE: 0,
        WETWARE_ZONE: 0,
    };
    values[LEGACY_REGION_BY_BIOME[sample.primary]] += sample.primaryWeight;
    if (sample.secondary !== null)
        values[LEGACY_REGION_BY_BIOME[sample.secondary]] += sample.secondaryWeight;
    return Object.freeze({
        ashForest: values.ASH_FOREST,
        riverValley: values.RIVER_VALLEY,
        deadFarmland: values.DEAD_FARMLAND,
        ruinedSuburb: values.RUINED_SUBURB,
        craterField: values.CRATER_FIELD,
        industrialWasteland: values.INDUSTRIAL_WASTELAND,
        aiExcavationZone: 0,
        wetwareZone: 0,
    });
}
function sampleWorldRegionWeights(x, z, seed, _fields) {
    return projectMacroBiomeToLegacyRegions(sampleMacroBiomeAt(x, z, seed));
}
const REGION_SAMPLE_CACHE_LIMIT = 8_192;
const regionSampleCache = new Map();
function regionSampleCacheKey(x, z, seed) {
    if (seed !== WORLD_GENERATION_SEED || !Number.isInteger(x) || !Number.isInteger(z))
        return undefined;
    if (x < WORLD_MIN_COORDINATE || x > WORLD_MAX_COORDINATE || z < WORLD_MIN_COORDINATE || z > WORLD_MAX_COORDINATE)
        return undefined;
    return (x - WORLD_MIN_COORDINATE) * (WORLD_SIZE_METERS + 1) + (z - WORLD_MIN_COORDINATE);
}
function rememberRegionSample(cacheKey, sample) {
    if (cacheKey === undefined)
        return sample;
    regionSampleCache.set(cacheKey, sample);
    if (regionSampleCache.size > REGION_SAMPLE_CACHE_LIMIT) {
        const oldest = regionSampleCache.keys().next().value;
        if (oldest !== undefined)
            regionSampleCache.delete(oldest);
    }
    return sample;
}
export function sampleWorldRegionWeightsAt(x, z, seed = WORLD_GENERATION_SEED) {
    const cacheKey = regionSampleCacheKey(x, z, seed);
    const cached = cacheKey === undefined ? undefined : regionSampleCache.get(cacheKey);
    if (cached)
        return cached;
    const fields = sampleWorldEnvironmentalFieldsAt(x, z, seed);
    return rememberRegionSample(cacheKey, sampleWorldRegionWeights(x, z, seed, fields));
}
export const getWorldRegionWeights = sampleWorldRegionWeightsAt;
export const sampleWorldRegionsAt = sampleWorldRegionWeightsAt;
export function getWorldRegionWeight(weights, region) {
    switch (region) {
        case "ASH_FOREST": return weights.ashForest;
        case "RIVER_VALLEY": return weights.riverValley;
        case "DEAD_FARMLAND": return weights.deadFarmland;
        case "RUINED_SUBURB": return weights.ruinedSuburb;
        case "CRATER_FIELD": return weights.craterField;
        case "INDUSTRIAL_WASTELAND": return weights.industrialWasteland;
        case "AI_EXCAVATION_ZONE": return weights.aiExcavationZone;
        case "WETWARE_ZONE": return weights.wetwareZone;
    }
}
export function getDominantWorldRegion(weights) {
    let dominant = WORLD_REGION_IDS[0];
    let maximum = getWorldRegionWeight(weights, dominant);
    for (const region of WORLD_REGION_IDS.slice(1)) {
        const value = getWorldRegionWeight(weights, region);
        if (value > maximum) {
            dominant = region;
            maximum = value;
        }
    }
    return dominant;
}
const CRATER_FIELD_FORMS = Object.freeze([
    { x: 1_780, z: 1_560, radiusX: 270, radiusZ: 205, rotation: -0.29, depth: 28, rimHeight: 7.5, salt: 751 },
    { x: 1_935, z: 1_505, radiusX: 118, radiusZ: 84, rotation: 0.31, depth: 7.2, rimHeight: 1.9, salt: 757 },
    { x: 1_610, z: 1_650, radiusX: 142, radiusZ: 91, rotation: -0.58, depth: 6.8, rimHeight: 1.7, salt: 761 },
    { x: 1_820, z: 1_760, radiusX: 86, radiusZ: 126, rotation: 0.73, depth: 5.3, rimHeight: 1.4, salt: 769 },
]);
function sampleCraterFormNormalizedDistance(x, z, form) {
    const cosine = Math.cos(form.rotation);
    const sine = Math.sin(form.rotation);
    const offsetX = x - form.x;
    const offsetZ = z - form.z;
    const localX = offsetX * cosine + offsetZ * sine;
    const localZ = -offsetX * sine + offsetZ * cosine;
    return Math.hypot(localX / form.radiusX, localZ / form.radiusZ);
}
function sampleCraterFormIrregularDistance(x, z, seed, form) {
    const radialDistance = sampleCraterFormNormalizedDistance(x, z, form);
    const boundaryNoise = coherentNoise(x + form.salt, z - form.salt, 31, form.salt, seed) * 0.11
        + coherentNoise(x - form.salt, z + form.salt, 67, form.salt + 2, seed) * 0.055;
    return radialDistance * (1 + boundaryNoise);
}
function sampleCraterGeologyPattern(x, z, seed) {
    let belt = 0;
    let interior = 0;
    for (const form of CRATER_FIELD_FORMS) {
        const distance = sampleCraterFormIrregularDistance(x, z, seed, form);
        belt = Math.max(belt, smoothstep(0.5, 0.72, distance) * (1 - smoothstep(0.96, 1.17, distance)));
        interior = Math.max(interior, 1 - smoothstep(0.18, 0.54, distance));
    }
    return { belt, interior };
}
const AI_EXCAVATION_CUTS = Object.freeze([
    Object.freeze({
        x: 1_588,
        z: -2_501,
        halfLength: 142,
        halfWidth: 64,
        rotation: 0.34,
        outerDepth: 1.3,
        middleDepth: 1.7,
        innerDepth: 2.3,
        salt: 781,
    }),
    Object.freeze({
        x: 1_820,
        z: -2_210,
        halfLength: 164,
        halfWidth: 70,
        rotation: -0.52,
        outerDepth: 1.2,
        middleDepth: 1.5,
        innerDepth: 1.9,
        salt: 787,
    }),
]);
const AI_EXCAVATION_SERVICE_CUT = Object.freeze({
    x: 1_704,
    z: -2_355,
    halfLength: 196,
    halfWidth: 8,
    rotation: 0.898,
    depth: 1.15,
    salt: 797,
});
/** Broken elliptical impact bowls overlap without introducing chunk-local seams. */
function sampleCraterFieldDisplacement(x, z, seed) {
    let displacement = 0;
    for (const form of CRATER_FIELD_FORMS) {
        const irregularDistance = sampleCraterFormIrregularDistance(x, z, seed, form);
        const bowl = (1 - smoothstep(0.2, 0.78, irregularDistance)) * form.depth;
        const rimBand = smoothstep(0.67, 0.9, irregularDistance) * (1 - smoothstep(0.93, 1.19, irregularDistance));
        const brokenRim = 0.7 + coherentNoise(x + form.salt, z, 42, form.salt + 4, seed) * 0.3;
        displacement += rimBand * form.rimHeight * brokenRim - bowl;
    }
    return clamp(displacement, -34, 9);
}
/** Signed distance to a rotated rectangle; negative values are inside the cut. */
function rotatedBoxSignedDistance(x, z, centerX, centerZ, halfLength, halfWidth, rotation) {
    const cosine = Math.cos(rotation);
    const sine = Math.sin(rotation);
    const offsetX = x - centerX;
    const offsetZ = z - centerZ;
    const localX = Math.abs(offsetX * cosine + offsetZ * sine) - halfLength;
    const localZ = Math.abs(-offsetX * sine + offsetZ * cosine) - halfWidth;
    const outside = Math.hypot(Math.max(localX, 0), Math.max(localZ, 0));
    return outside + Math.min(Math.max(localX, localZ), 0);
}
function sampleSteppedAiExcavationCut(x, z, seed, cut) {
    const signedDistance = rotatedBoxSignedDistance(x, z, cut.x, cut.z, cut.halfLength, cut.halfWidth, cut.rotation) + coherentNoise(x + cut.salt, z - cut.salt, 58, cut.salt, seed) * 1.35;
    const outerBench = 1 - smoothstep(0, 14, signedDistance);
    const middleBench = 1 - smoothstep(-20, -6, signedDistance);
    const innerBench = 1 - smoothstep(-44, -26, signedDistance);
    return -(outerBench * cut.outerDepth +
        middleBench * cut.middleDepth +
        innerBench * cut.innerDepth);
}
/** Geometric world-space benches plus a narrow connecting service cut. */
function sampleAiExcavationDisplacement(x, z, seed) {
    if (x < 1_360 || x > 2_070 || z < -2_680 || z > -1_990)
        return 0;
    let displacement = 0;
    for (const cut of AI_EXCAVATION_CUTS)
        displacement += sampleSteppedAiExcavationCut(x, z, seed, cut);
    const service = AI_EXCAVATION_SERVICE_CUT;
    const serviceDistance = rotatedBoxSignedDistance(x, z, service.x, service.z, service.halfLength, service.halfWidth, service.rotation) + coherentNoise(x - service.salt, z + service.salt, 46, service.salt, seed) * 0.7;
    displacement -= (1 - smoothstep(0, 9, serviceDistance)) * service.depth;
    return clamp(displacement, -9, 0);
}
function sampleBiomeTerrainProfileHeight(x, z, seed, base, biome) {
    const profile = BIOME_TERRAIN_PROFILES[biome];
    const localField = coherentNoise(x - 71, z + 53, profile.localNoiseScale, 703, seed);
    const local = localField * profile.roughness;
    const eroded = (Math.pow(Math.max(0, 1 - Math.abs(localField)), 3) - 0.18) * profile.erosion;
    const natural = base * (0.48 + profile.macroHeightAmplitude / 16) + local + eroded;
    const gradedDatum = base * 0.2 - (biome === "INDUSTRIAL_RUINS" ? 0.7 : 0);
    return mix(natural, gradedDatum, profile.flattening);
}
export function getAiExcavationInfluenceAt(x, z) {
    if (x < 1_160 || x > 2_260 || z < -2_900 || z > -1_800)
        return 0;
    return Math.max(radialInfluence(x, z, AI_EXCAVATION_CUTS[0].x, AI_EXCAVATION_CUTS[0].z, 80, 410), radialInfluence(x, z, AI_EXCAVATION_CUTS[1].x, AI_EXCAVATION_CUTS[1].z, 90, 440));
}
function shapeWorldUncarvedTerrainHeight(x, z, seed, base, macro) {
    const primaryHeight = sampleBiomeTerrainProfileHeight(x, z, seed, base, macro.primary);
    const secondaryHeight = macro.secondary === null
        ? primaryHeight
        : sampleBiomeTerrainProfileHeight(x, z, seed, base, macro.secondary);
    let shaped = mix(primaryHeight, secondaryHeight, macro.secondaryWeight);
    const groundZero = getMacroBiomeWeight(macro, "GROUND_ZERO");
    const directImpact = sampleDirectImpactRadiation(x, z, seed);
    const craterMask = smoothstep(0.08, 0.62, Math.max(groundZero, directImpact));
    if (craterMask > 0) {
        shaped += sampleCraterFieldDisplacement(x, z, seed) * craterMask;
        shaped -= craterMask * (1.1 + coherentNoise(x + 71, z - 43, 170, 719, seed) * 0.55);
    }
    const excavationMask = getAiExcavationInfluenceAt(x, z);
    if (excavationMask > 0) {
        shaped += sampleAiExcavationDisplacement(x, z, seed) * excavationMask;
        shaped -= excavationMask * (0.45 + coherentNoise(x - 101, z + 83, 180, 773, seed) * 0.14);
    }
    return shaped;
}
function getWorldUncarvedTerrainHeightAt(x, z, seed) {
    return shapeWorldUncarvedTerrainHeight(x, z, seed, getWorldBaseTerrainHeightAt(x, z, seed), sampleMacroBiomeAt(x, z, seed));
}
function shapeGeneratedRoadHeight(x, z, seed, uncarved) {
    let strongestInfluence = 0;
    let targetHeightTotal = 0;
    let targetWeightTotal = 0;
    for (const road of getProceduralRoadProfilesAt(x, z, seed)) {
        if (road.influence <= 0)
            continue;
        strongestInfluence = Math.max(strongestInfluence, road.influence);
        const weight = road.influence * road.influence;
        targetHeightTotal += getWorldUncarvedTerrainHeightAt(road.x, road.z, seed) * weight;
        targetWeightTotal += weight;
    }
    if (targetWeightTotal <= 0)
        return uncarved;
    return mix(uncarved, targetHeightTotal / targetWeightTotal, strongestInfluence * 0.62);
}
function getGeneratedWorldHeightAt(x, z, seed) {
    const base = getWorldBaseTerrainHeightAt(x, z, seed);
    const river = getWorldRiverProfileAt(x, z, seed);
    const macro = sampleMacroBiomeAt(x, z, seed);
    const uncarved = shapeWorldUncarvedTerrainHeight(x, z, seed, base, macro);
    const roadShaped = shapeGeneratedRoadHeight(x, z, seed, uncarved);
    return roadShaped - river.depth * river.excavation;
}
/** Terrain path used to choose POIs without depending on their own grading. */
function getWorldHeightBeforePoiGradingAt(x, z, seed) {
    if (seed !== WORLD_GENERATION_SEED)
        return getGeneratedWorldHeightAt(x, z, seed);
    const retained = preservationWeight(x, z);
    if (retained >= 1)
        return getLegacyHeightAt(x, z);
    const generated = getGeneratedWorldHeightAt(x, z, seed);
    if (retained <= 0)
        return generated;
    return mix(generated, getLegacyHeightAt(x, z), retained);
}
export function getWorldHeightAt(x, z, seed = WORLD_GENERATION_SEED) {
    assertFiniteCoordinates(x, z);
    const ungraded = getWorldHeightBeforePoiGradingAt(x, z, seed);
    return shapeWorldPoiPlotHeight(x, z, seed, ungraded);
}
export const getWorldTerrainHeightAt = getWorldHeightAt;
export function getWorldTerrainNormalAt(x, z, sampleDistance = 0.75, seed = WORLD_GENERATION_SEED) {
    if (!(sampleDistance > 0) || !Number.isFinite(sampleDistance))
        throw new RangeError("Terrain normal sample distance must be positive");
    const dx = getWorldHeightAt(x - sampleDistance, z, seed) - getWorldHeightAt(x + sampleDistance, z, seed);
    const dz = getWorldHeightAt(x, z - sampleDistance, seed) - getWorldHeightAt(x, z + sampleDistance, seed);
    const up = sampleDistance * 2;
    const length = Math.hypot(dx, up, dz) || 1;
    return Object.freeze([dx / length, up / length, dz / length]);
}
function getWorldTerrainNormalBeforePoiGradingAt(x, z, sampleDistance, seed) {
    const dx = getWorldHeightBeforePoiGradingAt(x - sampleDistance, z, seed)
        - getWorldHeightBeforePoiGradingAt(x + sampleDistance, z, seed);
    const dz = getWorldHeightBeforePoiGradingAt(x, z - sampleDistance, seed)
        - getWorldHeightBeforePoiGradingAt(x, z + sampleDistance, seed);
    const up = sampleDistance * 2;
    const length = Math.hypot(dx, up, dz) || 1;
    return Object.freeze([dx / length, up / length, dz / length]);
}
const surfaceProfile = (values) => Object.freeze(values);
const BIOME_SURFACE_PROFILES = Object.freeze({
    ASHLANDS: surfaceProfile([0.22, 0.58, 0.1, 0.09, 0.01]),
    GROUND_ZERO: surfaceProfile([0.04, 0.03, 0.92, 0.01, 0]),
    FERTILE_PRESERVE: surfaceProfile([0.08, 0.01, 0.25, 0.12, 0.54]),
    NUCLEAR_FOREST: surfaceProfile([0.08, 0.015, 0.43, 0.09, 0.385]),
    RUINED_CIVILIZATION: surfaceProfile([0.43, 0.17, 0.16, 0.22, 0.02]),
    INDUSTRIAL_RUINS: surfaceProfile([0.64, 0.18, 0.13, 0.045, 0.005]),
});
function getGeneratedTerrainSurfaceWeights(x, z, seed) {
    const macro = sampleMacroBiomeAt(x, z, seed);
    const primary = BIOME_SURFACE_PROFILES[macro.primary];
    const secondary = macro.secondary === null ? primary : BIOME_SURFACE_PROFILES[macro.secondary];
    const profile = primary.map((value, index) => mix(value, secondary[index], macro.secondaryWeight));
    const excavation = getAiExcavationInfluenceAt(x, z);
    const excavationDisturbance = excavation > 0
        ? clamp01(-sampleAiExcavationDisplacement(x, z, seed) / 6) * excavation
        : 0;
    const fusedGround = sampleDirectImpactRadiation(x, z, seed);
    const groundZero = getMacroBiomeWeight(macro, "GROUND_ZERO");
    const primaryCraterDistance = groundZero > 0
        ? sampleCraterFormNormalizedDistance(x, z, CRATER_FIELD_FORMS[0])
        : 2;
    const fusedInterior = (1 - smoothstep(0.18, 0.56, primaryCraterDistance)) * groundZero;
    const paleRim = smoothstep(0.5, 0.72, primaryCraterDistance)
        * (1 - smoothstep(0.96, 1.17, primaryCraterDistance))
        * groundZero;
    let dirt = profile[0] + excavation * 0.28 + excavationDisturbance * 1.55 + fusedGround * 0.12;
    let ash = profile[1] + fusedGround * 0.12 + paleRim * 1.35;
    let darkSoil = profile[2] + fusedGround * 1.25 + fusedInterior * 1.25;
    let deadGrass = profile[3] * (1 - excavation * 0.9);
    let sparseGreen = profile[4] * (1 - excavation * 0.96) * (1 - fusedGround * 0.94);
    const naturalTotal = dirt + ash + darkSoil + deadGrass + sparseGreen || 1;
    dirt /= naturalTotal;
    ash /= naturalTotal;
    darkSoil /= naturalTotal;
    deadGrass /= naturalTotal;
    sparseGreen /= naturalTotal;
    const road = getWorldRoadInfluenceAt(x, z, seed);
    const riverbed = getWorldRiverProfileAt(x, z, seed).excavation;
    const natural = (1 - road) * (1 - riverbed);
    return Object.freeze([
        dirt * natural,
        ash * natural,
        darkSoil * natural,
        deadGrass * natural,
        sparseGreen * natural,
        road * (1 - riverbed),
        riverbed,
    ]);
}
export function getWorldTerrainSurfaceWeights(x, z, seed = WORLD_GENERATION_SEED) {
    if (seed !== WORLD_GENERATION_SEED)
        return getGeneratedTerrainSurfaceWeights(x, z, seed);
    const retained = preservationWeight(x, z);
    if (retained >= 1)
        return getLegacyTerrainSurfaceWeights(x, z);
    const generated = getGeneratedTerrainSurfaceWeights(x, z, seed);
    if (retained <= 0)
        return generated;
    const legacy = getLegacyTerrainSurfaceWeights(x, z);
    return Object.freeze(generated.map((value, index) => mix(value, legacy[index], retained)));
}
export function getWorldRiverWaterHeightAt(x, z, seed = WORLD_GENERATION_SEED) {
    assertFiniteCoordinates(x, z);
    const retained = seed === WORLD_GENERATION_SEED ? preservationWeight(x, z) : 0;
    if (retained >= 1)
        return getLegacyRiverWaterHeightAt(x, z);
    const course = getWorldRiverCourseGeometryAt(z, seed);
    // Water follows the broad base at the course center. Keeping this independent
    // from region/terrain sampling prevents a river -> terrain -> region cycle.
    const generated = getWorldBaseTerrainHeightAt(course.x, course.z, seed) - course.depth + RIVER_PATH.waterDepth;
    if (retained <= 0)
        return generated;
    return mix(generated, getLegacyRiverWaterHeightAt(x, z), retained);
}
export function getWorldRiverCourseAt(z, seed = WORLD_GENERATION_SEED) {
    if (!Number.isFinite(z))
        throw new RangeError("World coordinate must be finite");
    const course = getWorldRiverCourseGeometryAt(z, seed);
    return Object.freeze({ ...course, waterHeight: getWorldRiverWaterHeightAt(course.x, z, seed) });
}
export function sampleWorldTerrainAt(x, z, seed = WORLD_GENERATION_SEED) {
    const macroBiome = sampleMacroBiomeAt(x, z, seed);
    return Object.freeze({
        x,
        z,
        baseHeight: getWorldBaseTerrainHeightAt(x, z, seed),
        height: getWorldHeightAt(x, z, seed),
        normal: getWorldTerrainNormalAt(x, z, 0.75, seed),
        surfaceWeights: getWorldTerrainSurfaceWeights(x, z, seed),
        regions: sampleWorldRegionWeightsAt(x, z, seed),
        macroBiome,
        radiation: sampleWorldRadiationAt(x, z, seed),
        ecology: sampleWorldEcologyAt(x, z, seed),
        fauna: sampleFaunaProfileAt(x, z, seed),
        road: getWorldRoadProfileAt(x, z, seed),
        river: getWorldRiverProfileAt(x, z, seed),
    });
}
const BIOME_COMPOSITION_PROFILES = Object.freeze({
    ASHLANDS: Object.freeze({ vegetationDensity: 0.2, geologyDensity: 0.28, humanRuinDensity: 0.18, aiInfrastructureDensity: 0.025, canopyDensity: 0.01, groundCoverDensity: 0.08 }),
    GROUND_ZERO: Object.freeze({ vegetationDensity: 0.012, geologyDensity: 0.94, humanRuinDensity: 0.1, aiInfrastructureDensity: 0.012, canopyDensity: 0, groundCoverDensity: 0.005 }),
    FERTILE_PRESERVE: Object.freeze({ vegetationDensity: 0.84, geologyDensity: 0.07, humanRuinDensity: 0.035, aiInfrastructureDensity: 0.24, canopyDensity: 0.38, groundCoverDensity: 0.96 }),
    NUCLEAR_FOREST: Object.freeze({ vegetationDensity: 0.98, geologyDensity: 0.24, humanRuinDensity: 0.08, aiInfrastructureDensity: 0.025, canopyDensity: 0.94, groundCoverDensity: 0.84 }),
    RUINED_CIVILIZATION: Object.freeze({ vegetationDensity: 0.12, geologyDensity: 0.16, humanRuinDensity: 0.96, aiInfrastructureDensity: 0.035, canopyDensity: 0.025, groundCoverDensity: 0.12 }),
    INDUSTRIAL_RUINS: Object.freeze({ vegetationDensity: 0.035, geologyDensity: 0.34, humanRuinDensity: 0.9, aiInfrastructureDensity: 0.16, canopyDensity: 0.005, groundCoverDensity: 0.025 }),
});
export function sampleWorldCompositionAt(x, z, seed = WORLD_GENERATION_SEED) {
    const macro = sampleMacroBiomeAt(x, z, seed);
    const primary = BIOME_COMPOSITION_PROFILES[macro.primary];
    const secondary = macro.secondary === null ? primary : BIOME_COMPOSITION_PROFILES[macro.secondary];
    const radiationSuppression = 1 - getRadiationIntensity(x, z, seed) * 0.78;
    return Object.freeze({
        vegetationDensity: clamp01(mix(primary.vegetationDensity, secondary.vegetationDensity, macro.secondaryWeight) * radiationSuppression),
        geologyDensity: clamp01(mix(primary.geologyDensity, secondary.geologyDensity, macro.secondaryWeight) + getAiExcavationInfluenceAt(x, z) * 0.48),
        humanRuinDensity: clamp01(mix(primary.humanRuinDensity, secondary.humanRuinDensity, macro.secondaryWeight)),
        aiInfrastructureDensity: clamp01(mix(primary.aiInfrastructureDensity, secondary.aiInfrastructureDensity, macro.secondaryWeight) + getAiExcavationInfluenceAt(x, z) * 0.7),
        baseBiome: macro.primary,
        canopyDensity: clamp01(mix(primary.canopyDensity, secondary.canopyDensity, macro.secondaryWeight) * radiationSuppression),
        groundCoverDensity: clamp01(mix(primary.groundCoverDensity, secondary.groundCoverDensity, macro.secondaryWeight) * radiationSuppression),
    });
}
const POI_DEFINITIONS = Object.freeze({
    abandoned_checkpoint: { origin: "human", footprintRadius: 31, exclusionRadius: 72, priority: 0.58 },
    small_ruin: { origin: "human", footprintRadius: 18, exclusionRadius: 58, priority: 0.35 },
    roadside_site: { origin: "human", footprintRadius: 24, exclusionRadius: 66, priority: 0.46 },
    suburban_ruin: { origin: "human", footprintRadius: 74, exclusionRadius: 154, priority: 0.65 },
    industrial_site: { origin: "human", footprintRadius: 46, exclusionRadius: 108, priority: 0.78 },
    gas_station: { origin: "human", footprintRadius: 52, exclusionRadius: 126, priority: 1.05 },
    wetware_relay: { origin: "ai", footprintRadius: 23, exclusionRadius: 92, priority: 0.86 },
    sensor_site: { origin: "ai", footprintRadius: 14, exclusionRadius: 64, priority: 0.55 },
    excavation_site: { origin: "ai", footprintRadius: 44, exclusionRadius: 112, priority: 0.82 },
    ai_farm: { origin: "ai", footprintRadius: 92, exclusionRadius: 184, priority: 1.16 },
    ecological_monitor: { origin: "ai", footprintRadius: 18, exclusionRadius: 72, priority: 0.66 },
});
const ROAD_ALIGNED_SOLID_PLOTS = Object.freeze({
    suburban_ruin: Object.freeze([
        Object.freeze({ x: -27, z: -25, halfWidth: 5, halfDepth: 4, yaw: 0.03 }),
        Object.freeze({ x: 28, z: -24, halfWidth: 4.4, halfDepth: 3.6, yaw: -0.04 }),
        Object.freeze({ x: -38, z: 31, halfWidth: 6.5, halfDepth: 4.8, yaw: 0.05 }),
        Object.freeze({ x: 31, z: 27, halfWidth: 5.2, halfDepth: 4.4, yaw: -0.06 }),
        Object.freeze({ x: -59, z: -24, halfWidth: 7, halfDepth: 6, yaw: 0.02 }),
        Object.freeze({ x: 60, z: 27, halfWidth: 9, halfDepth: 7, yaw: -0.03 }),
    ]),
    industrial_site: Object.freeze([
        Object.freeze({ x: -16, z: -23, halfWidth: 15, halfDepth: 9, yaw: 0.03 }),
        Object.freeze({ x: 17, z: 23, halfWidth: 13, halfDepth: 8, yaw: -0.04 }),
        Object.freeze({ x: -35, z: 20, halfWidth: 2.6, halfDepth: 2.6 }),
        Object.freeze({ x: 0, z: 16, halfWidth: 8, halfDepth: 1.8, yaw: 0.05 }),
        Object.freeze({ x: 27, z: -19, halfWidth: 5.5, halfDepth: 4.5, yaw: -0.08 }),
        Object.freeze({ x: -7, z: 25, halfWidth: 6, halfDepth: 5 }),
    ]),
});
const MAXIMUM_ROAD_ALIGNED_POI_RELOCATION = 123;
function roadAlignedSolidPlotsAreClear(archetype, siteX, siteZ, rotation, seed) {
    const cosine = Math.cos(rotation);
    const sine = Math.sin(rotation);
    for (const plot of ROAD_ALIGNED_SOLID_PLOTS[archetype]) {
        const x = siteX + plot.x * cosine - plot.z * sine;
        const z = siteZ + plot.x * sine + plot.z * cosine;
        const objectYaw = rotation + (plot.yaw ?? 0);
        const river = getWorldRiverProfileAt(x, z, seed);
        if (river.distance < river.width * 0.95 + Math.hypot(plot.halfWidth, plot.halfDepth) + 1.5)
            return false;
        for (const road of getProceduralRoadProfilesAt(x, z, seed)) {
            const relativeYaw = objectYaw - road.heading;
            const projectedHalfExtent = Math.abs(Math.sin(relativeYaw)) * plot.halfWidth
                + Math.abs(Math.cos(relativeYaw)) * plot.halfDepth;
            if (road.distance < road.width * 0.7 + projectedHalfExtent + 1.5)
                return false;
        }
    }
    return true;
}
const CANONICAL_AI_FARM_ID = createStableWorldObjectId({
    kind: "ai-poi-ai_farm",
    chunkX: worldToChunkCoordinate(-2_720),
    chunkZ: worldToChunkCoordinate(2_040),
    localIndex: "fertile-preserve-farm",
});
const CANONICAL_GAS_STATION_X = 1_080;
const CANONICAL_GAS_STATION_Z = mainRoadZAt(CANONICAL_GAS_STATION_X, WORLD_GENERATION_SEED) + 30;
const CANONICAL_GAS_STATION_ID = createStableWorldObjectId({
    kind: "human-poi-gas_station",
    chunkX: worldToChunkCoordinate(CANONICAL_GAS_STATION_X),
    chunkZ: worldToChunkCoordinate(CANONICAL_GAS_STATION_Z),
    localIndex: "heritage-highway-gas-station",
});
const CANONICAL_ROADSIDE_PROFILE = getWorldHighwayProfileAt(-1_000);
const CANONICAL_ROADSIDE_X = CANONICAL_ROADSIDE_PROFILE.x - Math.sin(CANONICAL_ROADSIDE_PROFILE.heading) * 18;
const CANONICAL_ROADSIDE_Z = CANONICAL_ROADSIDE_PROFILE.z + Math.cos(CANONICAL_ROADSIDE_PROFILE.heading) * 18;
const CANONICAL_ROADSIDE_SITE_ID = createStableWorldObjectId({
    kind: "human-poi-roadside_site",
    chunkX: worldToChunkCoordinate(CANONICAL_ROADSIDE_X),
    chunkZ: worldToChunkCoordinate(CANONICAL_ROADSIDE_Z),
    localIndex: "heritage-highway-roadside-wreck",
});
const CANONICAL_INDUSTRIAL_PROFILE = getWorldRoadProfileAt(2_180, -150);
const CANONICAL_INDUSTRIAL_X = CANONICAL_INDUSTRIAL_PROFILE.x;
const CANONICAL_INDUSTRIAL_Z = CANONICAL_INDUSTRIAL_PROFILE.z;
const CANONICAL_INDUSTRIAL_SITE_ID = createStableWorldObjectId({
    kind: "human-poi-industrial_site",
    chunkX: worldToChunkCoordinate(CANONICAL_INDUSTRIAL_X),
    chunkZ: worldToChunkCoordinate(CANONICAL_INDUSTRIAL_Z),
    localIndex: "industrial-core-complex",
});
const CANONICAL_RUINED_CIV_SITE_ID = createStableWorldObjectId({
    kind: "human-poi-suburban_ruin",
    chunkX: worldToChunkCoordinate(-1_820),
    chunkZ: worldToChunkCoordinate(760),
    localIndex: "ruined-civilization-core",
});
const CANONICAL_SEEDED_POIS = Object.freeze([
    Object.freeze({ id: "human-site:abandoned-checkpoint:01", origin: "human", archetype: "abandoned_checkpoint", x: -12, z: 5, rotation: 0.94, footprintRadius: 31, exclusionRadius: 48, priority: 2, authored: true }),
    Object.freeze({ id: "ai-site:wetware-relay:01", origin: "ai", archetype: "wetware_relay", x: 28, z: -48, rotation: -0.55, footprintRadius: 23, exclusionRadius: 48, priority: 2, authored: true }),
    Object.freeze({ id: CANONICAL_AI_FARM_ID, origin: "ai", archetype: "ai_farm", x: -2_720, z: 2_040, rotation: 0.08, footprintRadius: 92, exclusionRadius: 184, priority: 2.1, authored: false }),
    Object.freeze({ id: CANONICAL_GAS_STATION_ID, origin: "human", archetype: "gas_station", x: CANONICAL_GAS_STATION_X, z: CANONICAL_GAS_STATION_Z, rotation: Math.atan2(mainRoadDerivativeAt(CANONICAL_GAS_STATION_X, WORLD_GENERATION_SEED), 1), footprintRadius: 52, exclusionRadius: 126, priority: 2.05, authored: false }),
    Object.freeze({ id: CANONICAL_ROADSIDE_SITE_ID, origin: "human", archetype: "roadside_site", x: CANONICAL_ROADSIDE_X, z: CANONICAL_ROADSIDE_Z, rotation: CANONICAL_ROADSIDE_PROFILE.heading, footprintRadius: 24, exclusionRadius: 66, priority: 1.2, authored: false }),
    Object.freeze({ id: CANONICAL_INDUSTRIAL_SITE_ID, origin: "human", archetype: "industrial_site", x: CANONICAL_INDUSTRIAL_X, z: CANONICAL_INDUSTRIAL_Z, rotation: CANONICAL_INDUSTRIAL_PROFILE.heading, footprintRadius: 46, exclusionRadius: 108, priority: 1.85, authored: false }),
    Object.freeze({ id: CANONICAL_RUINED_CIV_SITE_ID, origin: "human", archetype: "suburban_ruin", x: -1_820, z: 760, rotation: getWorldRoadProfileAt(-1_820, 760).heading, footprintRadius: 74, exclusionRadius: 154, priority: 1.8, authored: false }),
    Object.freeze({
        id: createStableWorldObjectId({
            kind: "ai-poi-wetware_relay",
            chunkX: 13,
            chunkZ: -40,
            localIndex: "wetware-macro-relay",
        }),
        origin: "ai",
        archetype: "wetware_relay",
        x: 864,
        z: -2_560,
        rotation: -0.24,
        footprintRadius: 23,
        exclusionRadius: 92,
        priority: 1.4,
        authored: false,
    }),
]);
let canonicalSeededPoiCache;
let canonicalTerrainSeededPoiCache;
function makePoi(id, origin, archetype, x, z, rotation, footprintRadius, exclusionRadius, priority, contextualScore, authored, seed, terrainHeight) {
    const chunkX = worldToChunkCoordinate(x);
    const chunkZ = worldToChunkCoordinate(z);
    const regions = sampleWorldRegionWeightsAt(x, z, seed);
    const macro = sampleMacroBiomeAt(x, z, seed);
    const road = getWorldRoadProfileAt(x, z, seed);
    const highway = mainRoadProfile(x, z, seed);
    const river = getWorldRiverProfileAt(x, z, seed);
    const ecology = sampleWorldEcologyAt(x, z, seed);
    const radiation = getRadiationIntensity(x, z, seed);
    const overlayTags = [];
    if (river.distance < river.width * 1.8)
        overlayTags.push("RIVER");
    if (highway.distance < highway.width * 2.5)
        overlayTags.push("HIGHWAY");
    else if (road.distance < road.width * 2.5)
        overlayTags.push("ROAD");
    if (radiation >= 0.08)
        overlayTags.push("RADIATION");
    if (ecology.protectedArea)
        overlayTags.push("AI_PROTECTED_AREA");
    if (origin === "human")
        overlayTags.push("HUMAN_POI");
    if (archetype === "ai_farm")
        overlayTags.push("AI_FARM");
    if (archetype === "excavation_site")
        overlayTags.push("AI_EXCAVATION");
    if (archetype === "wetware_relay")
        overlayTags.push("WETWARE_SITE");
    return Object.freeze({
        id,
        origin,
        archetype,
        x,
        y: terrainHeight ?? getWorldHeightAt(x, z, seed),
        z,
        rotation,
        footprintRadius,
        exclusionRadius,
        priority,
        streamChunkX: chunkX,
        streamChunkZ: chunkZ,
        regionWeights: regions,
        baseBiome: macro.primary,
        overlayTags: Object.freeze(overlayTags),
        roadDistance: road.distance,
        riverDistance: river.distance,
        contextualScore,
        authored,
    });
}
function getCanonicalSeededPois(seed, terrainIndependent = false) {
    if (seed !== WORLD_GENERATION_SEED)
        return Object.freeze([]);
    const cached = terrainIndependent ? canonicalTerrainSeededPoiCache : canonicalSeededPoiCache;
    if (cached)
        return cached;
    const sites = Object.freeze(CANONICAL_SEEDED_POIS.map((site) => makePoi(site.id, site.origin, site.archetype, site.x, site.z, site.rotation, site.footprintRadius, site.exclusionRadius, site.priority, 1, site.authored, seed, terrainIndependent ? getWorldHeightBeforePoiGradingAt(site.x, site.z, seed) : undefined)));
    if (terrainIndependent)
        canonicalTerrainSeededPoiCache = sites;
    else
        canonicalSeededPoiCache = sites;
    return sites;
}
export const WORLD_REVIEW_DESTINATION_IDS = Object.freeze([
    "START",
    "INTERACTION_TEST",
    "ASHLANDS_CORE",
    "GROUND_ZERO_CORE",
    "FERTILE_CORE",
    "AI_FARM",
    "NUCLEAR_FOREST_CORE",
    "RUINED_CIV_CORE",
    "INDUSTRIAL_CORE",
    "HIGHWAY_GAS_STATION",
    "WETWARE_DATACENTER",
    "ASH_TO_FOREST_TRANSITION",
    "FERTILE_TO_ASH_TRANSITION",
    "GROUND_ZERO_BOUNDARY",
]);
const reviewDestination = (id, label, x, z, expectedBiome, poiId) => Object.freeze({ id, label, x, z, expectedBiome, ...(poiId === undefined ? {} : { poiId }) });
/** Canonical seed-734021 Generation-V2 locations in the normal streamed world. */
export const WORLD_REVIEW_DESTINATIONS = Object.freeze({
    START: reviewDestination("START", "Start", 0, 0, "ASHLANDS", "human-site:abandoned-checkpoint:01"),
    INTERACTION_TEST: reviewDestination("INTERACTION_TEST", "Interaction test", -5.3, 18.62, "ASHLANDS", "human-site:abandoned-checkpoint:01"),
    ASHLANDS_CORE: reviewDestination("ASHLANDS_CORE", "Ashlands core", 624, -656, "ASHLANDS"),
    GROUND_ZERO_CORE: reviewDestination("GROUND_ZERO_CORE", "Ground Zero core", 1_888, 1_632, "GROUND_ZERO"),
    FERTILE_CORE: reviewDestination("FERTILE_CORE", "Fertile Preserve core", -2_848, 1_696, "FERTILE_PRESERVE"),
    AI_FARM: reviewDestination("AI_FARM", "AI-managed farm", -2_720, 2_016, "FERTILE_PRESERVE", CANONICAL_AI_FARM_ID),
    NUCLEAR_FOREST_CORE: reviewDestination("NUCLEAR_FOREST_CORE", "Nuclear Forest core", -1_632, -1_504, "NUCLEAR_FOREST"),
    RUINED_CIV_CORE: reviewDestination("RUINED_CIV_CORE", "Ruined Civilization core", -1_824, 736, "RUINED_CIVILIZATION", CANONICAL_RUINED_CIV_SITE_ID),
    INDUSTRIAL_CORE: reviewDestination("INDUSTRIAL_CORE", "Industrial Ruins core", 2_208, -160, "INDUSTRIAL_RUINS", CANONICAL_INDUSTRIAL_SITE_ID),
    HIGHWAY_GAS_STATION: reviewDestination("HIGHWAY_GAS_STATION", "Highway gas station", 1_056, 672, "RUINED_CIVILIZATION", CANONICAL_GAS_STATION_ID),
    WETWARE_DATACENTER: reviewDestination("WETWARE_DATACENTER", "Wetware datacenter", 864, -2_490, "NUCLEAR_FOREST", createStableWorldObjectId({ kind: "ai-poi-wetware_relay", chunkX: 13, chunkZ: -40, localIndex: "wetware-macro-relay" })),
    ASH_TO_FOREST_TRANSITION: Object.freeze({ ...reviewDestination("ASH_TO_FOREST_TRANSITION", "Ashlands to Nuclear Forest transition", -811.625, -231.375, "ASHLANDS"), expectedSecondaryBiome: "NUCLEAR_FOREST" }),
    FERTILE_TO_ASH_TRANSITION: Object.freeze({ ...reviewDestination("FERTILE_TO_ASH_TRANSITION", "Fertile Preserve to Ashlands transition", -3_075.625, 2_444.625, "FERTILE_PRESERVE"), expectedSecondaryBiome: "ASHLANDS" }),
    GROUND_ZERO_BOUNDARY: Object.freeze({ ...reviewDestination("GROUND_ZERO_BOUNDARY", "Ground Zero boundary", 2_356.375, 1_980.625, "GROUND_ZERO"), expectedSecondaryBiome: "ASHLANDS" }),
});
export function getWorldReviewDestination(id) {
    return WORLD_REVIEW_DESTINATIONS[id];
}
export const WORLD_DRONE_PATROL_ANCHORS = Object.freeze(ECOLOGICAL_PATROL_POINTS.map((point, index) => Object.freeze({
    id: createStableWorldObjectId({
        kind: "drone-patrol-anchor",
        chunkX: worldToChunkCoordinate(point.x),
        chunkZ: worldToChunkCoordinate(point.z),
        localIndex: index,
    }),
    x: point.x,
    z: point.z,
    coverageRadius: point.radius,
})));
export const WORLD_DRONE_PATROL_ROUTES = Object.freeze([
    Object.freeze({
        id: createStableWorldObjectId({ kind: "drone-patrol-route", chunkX: -43, chunkZ: 31, localIndex: "fertile-ring" }),
        anchorIds: Object.freeze(WORLD_DRONE_PATROL_ANCHORS.map((anchor) => anchor.id)),
        closed: true,
    }),
]);
export function getDronePatrolAnchorsInBounds(bounds) {
    return Object.freeze(WORLD_DRONE_PATROL_ANCHORS.filter((anchor) => (anchor.x >= bounds.minX && anchor.x < bounds.maxX && anchor.z >= bounds.minZ && anchor.z < bounds.maxZ)));
}
function selectHighestScore(entries) {
    let winner = entries[0];
    for (const entry of entries.slice(1))
        if (entry[1] > winner[1])
            winner = entry;
    return winner;
}
function createRawPoiCandidate(cellX, cellZ, origin, seed, candidateVariant = 0, terrainIndependent = false) {
    const salt = (origin === "human" ? 811 : 877) + candidateVariant * 131;
    const x = (cellX + 0.5 + (randomUnit(cellX, cellZ, salt, seed) - 0.5) * 0.62) * WORLD_POI_CELL_SIZE;
    const z = (cellZ + 0.5 + (randomUnit(cellX, cellZ, salt + 1, seed) - 0.5) * 0.62) * WORLD_POI_CELL_SIZE;
    if (!isWorldPositionInBounds(x, z))
        return undefined;
    const regions = sampleWorldRegionWeightsAt(x, z, seed);
    const macro = sampleMacroBiomeAt(x, z, seed);
    if (candidateVariant > 0
        && origin === "human"
        && Math.max(getMacroBiomeWeight(macro, "RUINED_CIVILIZATION"), getMacroBiomeWeight(macro, "INDUSTRIAL_RUINS")) < 0.5)
        return undefined;
    const road = getWorldRoadProfileAt(x, z, seed);
    const river = getWorldRiverProfileAt(x, z, seed);
    if (river.distance < river.width * 0.95)
        return undefined;
    const selected = origin === "human"
        ? selectHighestScore([
            ["abandoned_checkpoint", (1 - smoothstep(8, 62, road.distance)) * (0.65 + regions.deadFarmland + regions.ruinedSuburb)],
            ["small_ruin", 0.12 + regions.ashForest * 0.24 + regions.deadFarmland * 0.42 + regions.ruinedSuburb * 0.2],
            ["roadside_site", (1 - smoothstep(24, 120, road.distance)) * (0.58 + regions.deadFarmland + regions.industrialWasteland * 0.45)],
            ["suburban_ruin", getMacroBiomeWeight(macro, "RUINED_CIVILIZATION") * 4.2 * (0.42 + (1 - smoothstep(80, 240, road.distance)))],
            ["industrial_site", getMacroBiomeWeight(macro, "INDUSTRIAL_RUINS") * 3.8 * (0.45 + (1 - smoothstep(90, 280, road.distance)))],
            ["gas_station", 0],
        ])
        : selectHighestScore([
            ["wetware_relay", 0],
            ["sensor_site", 0.1 + getAiExcavationInfluenceAt(x, z) * 0.85],
            ["excavation_site", getAiExcavationInfluenceAt(x, z) * 3.25],
            ["ai_farm", 0],
            ["ecological_monitor", getMacroBiomeWeight(macro, "FERTILE_PRESERVE") * 1.65],
        ]);
    const [archetype, contextualScore] = selected;
    const chance = origin === "human"
        ? archetype === "suburban_ruin" || archetype === "industrial_site"
            ? clamp(0.45 + contextualScore * 0.12, 0.45, 0.96)
            : archetype === "roadside_site"
                ? clamp(0.09 + contextualScore * 0.18, 0.09, 0.38)
                : clamp(0.015 + contextualScore * 0.115, 0.015, 0.24)
        : clamp(0.004 + contextualScore * 0.06, 0.004, 0.12);
    if (randomUnit(cellX, cellZ, salt + 2, seed) >= chance)
        return undefined;
    const definition = POI_DEFINITIONS[archetype];
    if ((archetype === "abandoned_checkpoint" || archetype === "roadside_site") && road.distance > road.width * 0.55 + 9)
        return undefined;
    if (archetype === "suburban_ruin" && road.distance > road.width * 0.55 + 18)
        return undefined;
    if (archetype === "industrial_site" && road.distance > road.width * 0.55 + 30)
        return undefined;
    let siteX = x;
    let siteZ = z;
    let semanticRotation = origin === "human" && (archetype === "abandoned_checkpoint"
        || archetype === "roadside_site"
        || archetype === "suburban_ruin"
        || archetype === "industrial_site"
        || archetype === "gas_station") ? road.heading : randomUnit(cellX, cellZ, salt + 3, seed) * Math.PI * 2;
    if (archetype === "suburban_ruin" || archetype === "industrial_site") {
        const regionalGrid = archetype === "suburban_ruin"
            ? road.roadId === "settlement_grid_longitudinal" || road.roadId === "settlement_grid_cross"
            : road.roadId === "industrial_grid_longitudinal" || road.roadId === "industrial_grid_cross";
        const anchor = regionalGrid
            ? regionalGridPoiAnchorAt(archetype, x, z, seed)
            : Object.freeze({ x: road.x, z: road.z, heading: road.heading });
        siteX = anchor.x;
        siteZ = anchor.z;
        semanticRotation = anchor.heading;
        if (!roadAlignedSolidPlotsAreClear(archetype, siteX, siteZ, semanticRotation, seed))
            return undefined;
    }
    const siteMacro = sampleMacroBiomeAt(siteX, siteZ, seed);
    const siteRiver = getWorldRiverProfileAt(siteX, siteZ, seed);
    if (siteX - definition.footprintRadius < WORLD_MIN_COORDINATE ||
        siteX + definition.footprintRadius >= WORLD_MAX_COORDINATE ||
        siteZ - definition.footprintRadius < WORLD_MIN_COORDINATE ||
        siteZ + definition.footprintRadius >= WORLD_MAX_COORDINATE ||
        siteRiver.distance < siteRiver.width * 0.95 ||
        getWorldTerrainNormalBeforePoiGradingAt(siteX, siteZ, 4, seed)[1] < 0.74)
        return undefined;
    if (archetype === "suburban_ruin" && getMacroBiomeWeight(siteMacro, "RUINED_CIVILIZATION") < 0.5)
        return undefined;
    if (archetype === "industrial_site" && getMacroBiomeWeight(siteMacro, "INDUSTRIAL_RUINS") < 0.5)
        return undefined;
    if (archetype === "excavation_site" && getAiExcavationInfluenceAt(siteX, siteZ) < 0.2)
        return undefined;
    if (archetype === "ecological_monitor" && getMacroBiomeWeight(siteMacro, "FERTILE_PRESERVE") < 0.5)
        return undefined;
    const chunkX = worldToChunkCoordinate(siteX);
    const chunkZ = worldToChunkCoordinate(siteZ);
    const id = createStableWorldObjectId({
        kind: `${origin}-poi-${archetype}`,
        chunkX,
        chunkZ,
        localIndex: `${cellX}:${cellZ}:${candidateVariant}`,
        seed,
    });
    return makePoi(id, definition.origin, archetype, siteX, siteZ, semanticRotation, definition.footprintRadius, definition.exclusionRadius, definition.priority + randomUnit(cellX, cellZ, salt + 4, seed) * 0.18, contextualScore, false, seed, terrainIndependent ? getWorldHeightBeforePoiGradingAt(siteX, siteZ, seed) : undefined);
}
export const WORLD_POI_TERRAIN_CACHE_LIMITS = Object.freeze({
    rawCandidates: 4_096,
    acceptedCells: 512,
    gradingCells: 512,
});
const terrainRawPoiCache = new Map();
const terrainAcceptedPoiCellCache = new Map();
const terrainGradingSiteCellCache = new Map();
export function getWorldPoiTerrainCacheUsage() {
    return Object.freeze({
        rawCandidates: terrainRawPoiCache.size,
        acceptedCells: terrainAcceptedPoiCellCache.size,
        gradingCells: terrainGradingSiteCellCache.size,
    });
}
function terrainPoiCellKey(cellX, cellZ, seed) {
    return `${seed}:${cellX}:${cellZ}`;
}
function getTerrainIndependentRawPoiCandidate(cellX, cellZ, origin, seed, candidateVariant) {
    const key = `${terrainPoiCellKey(cellX, cellZ, seed)}:${origin}:${candidateVariant}`;
    const cached = terrainRawPoiCache.get(key);
    if (cached !== undefined)
        return cached ?? undefined;
    const candidate = createRawPoiCandidate(cellX, cellZ, origin, seed, candidateVariant, true);
    terrainRawPoiCache.set(key, candidate ?? null);
    while (terrainRawPoiCache.size > WORLD_POI_TERRAIN_CACHE_LIMITS.rawCandidates) {
        const oldest = terrainRawPoiCache.keys().next().value;
        if (oldest === undefined)
            break;
        terrainRawPoiCache.delete(oldest);
    }
    return candidate;
}
function getTerrainAcceptedPoiSitesForCell(cellX, cellZ, seed) {
    const key = terrainPoiCellKey(cellX, cellZ, seed);
    const cached = terrainAcceptedPoiCellCache.get(key);
    if (cached)
        return cached;
    const sites = [];
    for (const variant of [0, 1]) {
        const candidate = getTerrainIndependentRawPoiCandidate(cellX, cellZ, "human", seed, variant);
        if (candidate
            && (candidate.archetype === "suburban_ruin" || candidate.archetype === "industrial_site")
            && winsPoiSpacing(candidate, cellX, cellZ, seed, true))
            sites.push(candidate);
    }
    const frozen = Object.freeze(sites);
    terrainAcceptedPoiCellCache.set(key, frozen);
    while (terrainAcceptedPoiCellCache.size > WORLD_POI_TERRAIN_CACHE_LIMITS.acceptedCells) {
        const oldest = terrainAcceptedPoiCellCache.keys().next().value;
        if (oldest === undefined)
            break;
        terrainAcceptedPoiCellCache.delete(oldest);
    }
    return frozen;
}
const SUBURBAN_PLOT_GRADE_CORE_RADIUS = 77;
const INDUSTRIAL_PLOT_GRADE_CORE_RADIUS = 45;
const POI_PLOT_GRADE_FEATHER = 12;
const MAXIMUM_POI_PLOT_GRADE_RADIUS = SUBURBAN_PLOT_GRADE_CORE_RADIUS + POI_PLOT_GRADE_FEATHER;
function getTerrainGradingSitesForWorldCell(cellX, cellZ, seed) {
    const key = terrainPoiCellKey(cellX, cellZ, seed);
    const cached = terrainGradingSiteCellCache.get(key);
    if (cached)
        return cached;
    const minX = cellX * WORLD_POI_CELL_SIZE;
    const minZ = cellZ * WORLD_POI_CELL_SIZE;
    const maxX = minX + WORLD_POI_CELL_SIZE;
    const maxZ = minZ + WORLD_POI_CELL_SIZE;
    const sites = [];
    const addIfNearby = (site) => {
        if (site.archetype !== "suburban_ruin" && site.archetype !== "industrial_site")
            return;
        const radius = site.archetype === "suburban_ruin"
            ? SUBURBAN_PLOT_GRADE_CORE_RADIUS + POI_PLOT_GRADE_FEATHER
            : INDUSTRIAL_PLOT_GRADE_CORE_RADIUS + POI_PLOT_GRADE_FEATHER;
        const closestX = clamp(site.x, minX, maxX);
        const closestZ = clamp(site.z, minZ, maxZ);
        if (Math.hypot(site.x - closestX, site.z - closestZ) <= radius)
            sites.push(site);
    };
    for (const site of getCanonicalSeededPois(seed, true))
        addIfNearby(site);
    const queryReach = MAXIMUM_ROAD_ALIGNED_POI_RELOCATION + MAXIMUM_POI_PLOT_GRADE_RADIUS;
    const minimumSourceCellX = Math.floor((minX - queryReach) / WORLD_POI_CELL_SIZE);
    const maximumSourceCellX = Math.floor((maxX + queryReach - 1e-6) / WORLD_POI_CELL_SIZE);
    const minimumSourceCellZ = Math.floor((minZ - queryReach) / WORLD_POI_CELL_SIZE);
    const maximumSourceCellZ = Math.floor((maxZ + queryReach - 1e-6) / WORLD_POI_CELL_SIZE);
    for (let sourceCellZ = minimumSourceCellZ; sourceCellZ <= maximumSourceCellZ; sourceCellZ += 1) {
        for (let sourceCellX = minimumSourceCellX; sourceCellX <= maximumSourceCellX; sourceCellX += 1) {
            for (const site of getTerrainAcceptedPoiSitesForCell(sourceCellX, sourceCellZ, seed))
                addIfNearby(site);
        }
    }
    const frozen = Object.freeze(sites);
    terrainGradingSiteCellCache.set(key, frozen);
    while (terrainGradingSiteCellCache.size > WORLD_POI_TERRAIN_CACHE_LIMITS.gradingCells) {
        const oldest = terrainGradingSiteCellCache.keys().next().value;
        if (oldest === undefined)
            break;
        terrainGradingSiteCellCache.delete(oldest);
    }
    return frozen;
}
/** World-space cut/fill plateaus, derived from accepted POIs through the ungraded terrain path. */
function shapeWorldPoiPlotHeight(x, z, seed, ungraded) {
    let targetHeightTotal = 0;
    let weightTotal = 0;
    const accumulate = (site) => {
        if (site.archetype !== "suburban_ruin" && site.archetype !== "industrial_site")
            return;
        const coreRadius = site.archetype === "suburban_ruin"
            ? SUBURBAN_PLOT_GRADE_CORE_RADIUS
            : INDUSTRIAL_PLOT_GRADE_CORE_RADIUS;
        const distance = Math.hypot(x - site.x, z - site.z);
        if (distance >= coreRadius + POI_PLOT_GRADE_FEATHER)
            return;
        const weight = 1 - smoothstep(coreRadius, coreRadius + POI_PLOT_GRADE_FEATHER, distance);
        targetHeightTotal += site.y * weight;
        weightTotal += weight;
    };
    const terrainCellX = Math.floor(x / WORLD_POI_CELL_SIZE);
    const terrainCellZ = Math.floor(z / WORLD_POI_CELL_SIZE);
    for (const site of getTerrainGradingSitesForWorldCell(terrainCellX, terrainCellZ, seed))
        accumulate(site);
    if (weightTotal <= 0)
        return ungraded;
    return mix(ungraded, targetHeightTotal / weightTotal, clamp01(weightTotal));
}
function poiSpacing(a, b) {
    return Math.max(a.exclusionRadius, b.exclusionRadius, a.footprintRadius + b.footprintRadius + 8);
}
function winsPoiSpacing(candidate, cellX, cellZ, seed, terrainIndependent = false) {
    for (const seeded of getCanonicalSeededPois(seed, terrainIndependent)) {
        if (Math.hypot(candidate.x - seeded.x, candidate.z - seeded.z) < poiSpacing(candidate, seeded))
            return false;
    }
    for (let neighborZ = cellZ - 2; neighborZ <= cellZ + 2; neighborZ += 1) {
        for (let neighborX = cellX - 2; neighborX <= cellX + 2; neighborX += 1) {
            for (const origin of ["human", "ai"]) {
                const variants = origin === "human" ? [0, 1] : [0];
                for (const variant of variants) {
                    const other = terrainIndependent
                        ? getTerrainIndependentRawPoiCandidate(neighborX, neighborZ, origin, seed, variant)
                        : createRawPoiCandidate(neighborX, neighborZ, origin, seed, variant);
                    if (!other || other.id === candidate.id)
                        continue;
                    if (Math.hypot(candidate.x - other.x, candidate.z - other.z) >= poiSpacing(candidate, other))
                        continue;
                    if (other.priority > candidate.priority || (other.priority === candidate.priority && other.id < candidate.id))
                        return false;
                }
            }
        }
    }
    return true;
}
export function generateWorldPoisInBounds(bounds, seed = WORLD_GENERATION_SEED) {
    if (!Number.isFinite(bounds.minX) ||
        !Number.isFinite(bounds.minZ) ||
        !Number.isFinite(bounds.maxX) ||
        !Number.isFinite(bounds.maxZ) ||
        bounds.minX >= bounds.maxX ||
        bounds.minZ >= bounds.maxZ)
        throw new RangeError("POI query bounds must be finite and non-empty");
    const minX = Math.max(bounds.minX, WORLD_MIN_COORDINATE);
    const minZ = Math.max(bounds.minZ, WORLD_MIN_COORDINATE);
    const maxX = Math.min(bounds.maxX, WORLD_MAX_COORDINATE);
    const maxZ = Math.min(bounds.maxZ, WORLD_MAX_COORDINATE);
    if (minX >= maxX || minZ >= maxZ)
        return Object.freeze([]);
    const pois = [];
    for (const seeded of getCanonicalSeededPois(seed)) {
        if (seeded.x >= minX && seeded.x < maxX && seeded.z >= minZ && seeded.z < maxZ)
            pois.push(seeded);
    }
    const minimumCellX = Math.floor((minX - MAXIMUM_ROAD_ALIGNED_POI_RELOCATION) / WORLD_POI_CELL_SIZE);
    const maximumCellX = Math.floor((maxX + MAXIMUM_ROAD_ALIGNED_POI_RELOCATION - 1e-6) / WORLD_POI_CELL_SIZE);
    const minimumCellZ = Math.floor((minZ - MAXIMUM_ROAD_ALIGNED_POI_RELOCATION) / WORLD_POI_CELL_SIZE);
    const maximumCellZ = Math.floor((maxZ + MAXIMUM_ROAD_ALIGNED_POI_RELOCATION - 1e-6) / WORLD_POI_CELL_SIZE);
    for (let cellZ = minimumCellZ; cellZ <= maximumCellZ; cellZ += 1) {
        for (let cellX = minimumCellX; cellX <= maximumCellX; cellX += 1) {
            for (const origin of ["human", "ai"]) {
                const variants = origin === "human" ? [0, 1] : [0];
                for (const variant of variants) {
                    const candidate = createRawPoiCandidate(cellX, cellZ, origin, seed, variant);
                    if (!candidate || candidate.x < minX || candidate.x >= maxX || candidate.z < minZ || candidate.z >= maxZ)
                        continue;
                    if (winsPoiSpacing(candidate, cellX, cellZ, seed))
                        pois.push(candidate);
                }
            }
        }
    }
    return Object.freeze(pois.sort((a, b) => a.id.localeCompare(b.id)));
}
export const queryWorldPoisInBounds = generateWorldPoisInBounds;
export function generateWorldPoisForChunk(chunkX, chunkZ, seed = WORLD_GENERATION_SEED) {
    return generateWorldPoisInBounds(getWorldChunkBounds(chunkX, chunkZ), seed);
}
export const getWorldPoisForChunk = generateWorldPoisForChunk;
function conflictsWithPoi(x, z, pois, margin) {
    return pois.some((poi) => Math.hypot(x - poi.x, z - poi.z) < poi.footprintRadius + margin);
}
function selectCandidateBiome(macro, roll) {
    return macro.secondary !== null && roll < macro.secondaryWeight ? macro.secondary : macro.primary;
}
function selectVegetationArchetype(biome, roll) {
    switch (biome) {
        case "ASHLANDS": return roll < 0.38 ? "burned_tree" : roll < 0.72 ? "dead_tree" : "dead_shrub";
        case "GROUND_ZERO": return roll < 0.18 ? "burned_tree" : roll < 0.34 ? "dead_tree" : "dead_shrub";
        case "FERTILE_PRESERVE": return roll < 0.34 ? "living_tree" : roll < 0.57 ? "living_shrub" : "grass";
        case "NUCLEAR_FOREST": return roll < 0.56
            ? "nuclear_tree"
            : roll < 0.64
                ? "dead_giant"
                : roll < 0.72
                    ? "fallen_log"
                    : "understory";
        case "RUINED_CIVILIZATION": return roll < 0.22 ? "dead_tree" : roll < 0.58 ? "dead_shrub" : "sparse_tree";
        case "INDUSTRIAL_RUINS": return roll < 0.36 ? "dead_tree" : "dead_shrub";
    }
}
function generateStaticCandidates(chunkX, chunkZ, family, pois, seed) {
    const bounds = getWorldChunkBounds(chunkX, chunkZ);
    const spacing = family === "vegetation" ? 8 : 32;
    const salt = family === "vegetation" ? 1_101 : 1_211;
    const candidates = [];
    const firstCellX = Math.floor(bounds.minX / spacing);
    const firstCellZ = Math.floor(bounds.minZ / spacing);
    const cellsPerAxis = WORLD_CHUNK_SIZE / spacing;
    for (let localZ = 0; localZ < cellsPerAxis; localZ += 1) {
        for (let localX = 0; localX < cellsPerAxis; localX += 1) {
            const cellX = firstCellX + localX;
            const cellZ = firstCellZ + localZ;
            const x = (cellX + 0.5 + (randomUnit(cellX, cellZ, salt, seed) - 0.5) * 0.72) * spacing;
            const z = (cellZ + 0.5 + (randomUnit(cellX, cellZ, salt + 1, seed) - 0.5) * 0.72) * spacing;
            const composition = sampleWorldCompositionAt(x, z, seed);
            const regions = sampleWorldRegionWeightsAt(x, z, seed);
            const macro = sampleMacroBiomeAt(x, z, seed);
            const candidateBiome = selectCandidateBiome(macro, randomUnit(cellX, cellZ, salt + 5, seed));
            const craterRubble = family === "geology" ? getMacroBiomeWeight(macro, "GROUND_ZERO") : 0;
            const craterGeology = family === "geology" && craterRubble > 0
                ? sampleCraterGeologyPattern(x, z, seed)
                : { belt: 0, interior: 0 };
            const proceduralArea = seed === WORLD_GENERATION_SEED ? 1 - preservationWeight(x, z) : 1;
            const density = (family === "vegetation"
                ? composition.vegetationDensity * 0.88
                : composition.geologyDensity
                    * (0.58 + craterRubble * 0.22 + craterGeology.belt * 0.18)
                    * (1 - craterGeology.interior * 0.34)) * proceduralArea;
            const variant = randomUnit(cellX, cellZ, salt + 2, seed);
            if (variant >= density)
                continue;
            const road = getWorldRoadProfileAt(x, z, seed);
            const river = getWorldRiverProfileAt(x, z, seed);
            const clearance = family === "vegetation" ? 4.5 : 3.5;
            if (road.distance < road.width * 0.55 + clearance || river.distance < river.width * 0.58 + clearance || conflictsWithPoi(x, z, pois, clearance))
                continue;
            const archetype = family === "vegetation"
                ? selectVegetationArchetype(candidateBiome, randomUnit(cellX, cellZ, salt + 6, seed))
                : variant < density * mix(0.12, 0.24, craterRubble)
                    ? "formation"
                    : variant < density * mix(0.36, 0.62, craterRubble)
                        ? "boulder"
                        : variant < density * mix(0.68, 0.86, craterRubble) ? "medium" : "small";
            const localIndex = `${cellX}:${cellZ}`;
            candidates.push(Object.freeze({
                id: createStableWorldObjectId({ kind: family, chunkX, chunkZ, localIndex, seed }),
                family,
                archetype,
                x,
                y: getWorldHeightAt(x, z, seed),
                z,
                rotation: randomUnit(cellX, cellZ, salt + 3, seed) * Math.PI * 2,
                scale: (0.78 + randomUnit(cellX, cellZ, salt + 4, seed) * 0.55)
                    * (1 + craterRubble * 0.35 + craterGeology.belt * 1.05 - craterGeology.interior * 0.08)
                    * (candidateBiome === "NUCLEAR_FOREST" ? 0.94 : 1),
                streamChunkX: chunkX,
                streamChunkZ: chunkZ,
                dominantRegion: getDominantWorldRegion(regions),
                baseBiome: macro.primary,
            }));
        }
    }
    return Object.freeze(candidates);
}
function averageRegionWeights(samples) {
    const sums = samples.reduce((result, sample) => ({
        ashForest: result.ashForest + sample.ashForest,
        riverValley: result.riverValley + sample.riverValley,
        deadFarmland: result.deadFarmland + sample.deadFarmland,
        ruinedSuburb: result.ruinedSuburb + sample.ruinedSuburb,
        craterField: result.craterField + sample.craterField,
        industrialWasteland: result.industrialWasteland + sample.industrialWasteland,
        aiExcavationZone: result.aiExcavationZone + sample.aiExcavationZone,
        wetwareZone: result.wetwareZone + sample.wetwareZone,
    }), { ashForest: 0, riverValley: 0, deadFarmland: 0, ruinedSuburb: 0, craterField: 0, industrialWasteland: 0, aiExcavationZone: 0, wetwareZone: 0 });
    const count = samples.length || 1;
    return Object.freeze({
        ashForest: sums.ashForest / count,
        riverValley: sums.riverValley / count,
        deadFarmland: sums.deadFarmland / count,
        ruinedSuburb: sums.ruinedSuburb / count,
        craterField: sums.craterField / count,
        industrialWasteland: sums.industrialWasteland / count,
        aiExcavationZone: sums.aiExcavationZone / count,
        wetwareZone: sums.wetwareZone / count,
    });
}
export function summarizeWorldChunk(chunkX, chunkZ, pois = undefined, seed = WORLD_GENERATION_SEED) {
    const bounds = getWorldChunkBounds(chunkX, chunkZ);
    const chunkPois = pois ?? generateWorldPoisForChunk(chunkX, chunkZ, seed);
    const regionSamples = [];
    const biomeCounts = new Map();
    let minimumHeight = Number.POSITIVE_INFINITY;
    let maximumHeight = Number.NEGATIVE_INFINITY;
    let riverCoverage = 0;
    let roadCoverage = 0;
    let vegetationDensity = 0;
    let geologyDensity = 0;
    for (let sampleZ = 0; sampleZ < WORLD_CHUNK_SUMMARY_SAMPLE_GRID; sampleZ += 1) {
        for (let sampleX = 0; sampleX < WORLD_CHUNK_SUMMARY_SAMPLE_GRID; sampleX += 1) {
            const x = mix(bounds.minX, bounds.maxX, sampleX / (WORLD_CHUNK_SUMMARY_SAMPLE_GRID - 1));
            const z = mix(bounds.minZ, bounds.maxZ, sampleZ / (WORLD_CHUNK_SUMMARY_SAMPLE_GRID - 1));
            const regions = sampleWorldRegionWeightsAt(x, z, seed);
            const macro = sampleMacroBiomeAt(x, z, seed);
            const height = getWorldHeightAt(x, z, seed);
            const composition = sampleWorldCompositionAt(x, z, seed);
            regionSamples.push(regions);
            biomeCounts.set(macro.primary, (biomeCounts.get(macro.primary) ?? 0) + 1);
            minimumHeight = Math.min(minimumHeight, height);
            maximumHeight = Math.max(maximumHeight, height);
            riverCoverage += getWorldRiverProfileAt(x, z, seed).influence;
            roadCoverage += getWorldRoadInfluenceAt(x, z, seed);
            vegetationDensity += composition.vegetationDensity;
            geologyDensity += composition.geologyDensity;
        }
    }
    const sampleCount = regionSamples.length;
    const regionWeights = averageRegionWeights(regionSamples);
    const dominantBiome = [...biomeCounts.entries()].sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))[0]?.[0]
        ?? sampleMacroBiomeAt((bounds.minX + bounds.maxX) * 0.5, (bounds.minZ + bounds.maxZ) * 0.5, seed).primary;
    return Object.freeze({
        chunkId: getWorldChunkId(chunkX, chunkZ, seed),
        centerX: (bounds.minX + bounds.maxX) * 0.5,
        centerZ: (bounds.minZ + bounds.maxZ) * 0.5,
        regionWeights,
        dominantRegion: getDominantWorldRegion(regionWeights),
        dominantBiome,
        minimumHeight,
        maximumHeight,
        riverCoverage: riverCoverage / sampleCount,
        roadCoverage: roadCoverage / sampleCount,
        vegetationDensity: vegetationDensity / sampleCount,
        geologyDensity: geologyDensity / sampleCount,
        humanPoiCount: chunkPois.filter((poi) => poi.origin === "human").length,
        aiPoiCount: chunkPois.filter((poi) => poi.origin === "ai").length,
        sampleCount,
    });
}
export function generateWorldChunk(chunkX, chunkZ, seed = WORLD_GENERATION_SEED) {
    assertValidChunk(chunkX, chunkZ);
    const bounds = getWorldChunkBounds(chunkX, chunkZ);
    const pointsOfInterest = generateWorldPoisForChunk(chunkX, chunkZ, seed);
    const poiHalo = 128;
    const nearbyPointsOfInterest = generateWorldPoisInBounds({
        minX: bounds.minX - poiHalo,
        minZ: bounds.minZ - poiHalo,
        maxX: bounds.maxX + poiHalo,
        maxZ: bounds.maxZ + poiHalo,
    }, seed);
    const vegetationCandidates = generateStaticCandidates(chunkX, chunkZ, "vegetation", nearbyPointsOfInterest, seed);
    const geologyCandidates = generateStaticCandidates(chunkX, chunkZ, "geology", nearbyPointsOfInterest, seed);
    const summary = summarizeWorldChunk(chunkX, chunkZ, pointsOfInterest, seed);
    const chunkId = getWorldChunkId(chunkX, chunkZ, seed);
    const signature = [
        chunkId,
        summary.dominantBiome,
        summary.dominantRegion,
        summary.minimumHeight.toFixed(5),
        summary.maximumHeight.toFixed(5),
        ...pointsOfInterest.map((poi) => poi.id),
        ...vegetationCandidates.map((candidate) => candidate.id),
        ...geologyCandidates.map((candidate) => candidate.id),
    ].join("|");
    const metadata = Object.freeze({
        generationVersion: WORLD_GENERATION_VERSION,
        worldSeed: seed,
        chunkId,
        chunk: Object.freeze({ x: chunkX, z: chunkZ }),
        bounds,
        contentFingerprint: compactHash(signature),
        generationLayers: GENERATION_LAYERS,
    });
    return Object.freeze({
        metadata,
        summary,
        pointsOfInterest,
        humanPois: Object.freeze(pointsOfInterest.filter((poi) => poi.origin === "human")),
        aiPois: Object.freeze(pointsOfInterest.filter((poi) => poi.origin === "ai")),
        vegetationCandidates,
        geologyCandidates,
    });
}
